(function() {
  'use strict';

  /*
    One viewer per page. Several homepage rows each load this file (the
    McDermott and Longoni tiles, the case tiles, Pool Ball Sets), and every
    copy used to run: its own modal, its own keydown and resize listeners.
    The first copy wins; it already re-scans on shopify:section:load, so a
    row that arrives later is picked up by it.
  */
  if (window.BCLongoniLightbox) return;

  var ROOT_SELECTOR = '#longoni-home-promo-section .longoni-promo-strip';
  var TRIGGER_SELECTOR = '[data-longoni-gallery-trigger]';
  var MIN_ZOOM = 1;
  var BUTTON_ZOOM = 2;
  var MAX_ZOOM = 3;
  var modal = null;
  var activeItems = [];
  /*
    A STATIC GALLERY (2026-09-23, the certificates page): a gallery whose
    items are documents, not products, says so with data-longoni-static on
    the gallery element. The viewer then shows no price and no ADD TO
    CART, keeps the title as plain text, preloads the neighbours of the
    frame it shows, and stands the item's thumbnail - blurred 2px - in the
    frame until the full-size file has decoded. Every product gallery is
    untouched: nothing here runs without the attribute.
  */
  var activeStatic = false;
  // WHICH SURFACE OPENED THE VIEWER (2026-09-29). Only the product page sets
  // it, and it decides one thing: the strip of thumbnails. On a tile that strip
  // is the product's options and is the point of the panel; on a product page
  // the shopper already has the thumbnail rail beside the frame, so the engine
  // does not draw a second one. Read from the gallery container, used in
  // renderThumbnails, nowhere else - the homepage galleries carry no such
  // attribute and behave exactly as before.
  var activeSurface = '';
  // WHETHER THIS GALLERY ASKS FOR THE WHOLE MEDIA LIST (2026-10-17). Set from
  // data-longoni-media-fetch on the gallery container: the catalogue cards
  // carry six photographs in their markup and the viewer reads the rest from
  // /products/<handle>.js when it opens. The homepage tiles do not set it and
  // behave exactly as before.
  var activeMediaFetch = false;
  var activeIndex = 0;
  var angleIndex = 0;
  var lastTrigger = null;
  var lockedScrollY = 0;
  var touchStartX = null;
  var zoomLevel = MIN_ZOOM;
  var panX = 0;
  var panY = 0;
  var isPanning = false;
  var panStartX = 0;
  var panStartY = 0;
  var startPanX = 0;
  var startPanY = 0;
  var activePointers = [];
  var pinchStartDistance = 0;
  var pinchStartZoom = MIN_ZOOM;
  var didPan = false;
  var swapOffset = 0;
  var showImageToken = 0;
  /*
    THE SLIDE PIPELINE (2026-10-14, the owner's brief "the tile loupe needs a
    proper slide pipeline"). Measured before it, on the McDermott G338 tile at
    1440 with the cache cold: a press on "next" wrote the next frame's 2048px
    original (75-389 KB, 2.7x the pixels the 625px stage draws) into the one
    <img> 210 ms into the slide animation, with nothing fetched or decoded
    ahead, so the stage went blank the instant src changed and faded an
    empty frame back in while the file downloaded - the picture landed
    843-1229 ms after the click (median 959) and the dot had moved at 210.
    Now: a frame is requested from the CDN at the size the stage draws
    (sizedUrl, the ladder below, times devicePixelRatio), fetched and
    decoded before anything on the stage changes, and then the src, the
    dots and the arrows change in one synchronous step with the new frame
    sliding into place - the stage is never blank and never animates an
    empty element. The two frames either side, and the neighbouring
    options' first frames, are warmed after every show, so the next press
    finds its file decoded. The 2048 original is fetched only when the
    viewer is zoomed past 1.3x, and swapped in the same way.
  */
  var SIZE_LADDER = [480, 640, 800, 1024, 1200, 1400, 1600, 2048];
  var FRAME_CACHE_MAX = 14;
  /*
    A REFUSED FRAME IS ASKED AGAIN, AND A FAILURE IS NEVER CACHED
    (2026-10-17, the owner's audit after the glove row's empty cards). warm()
    used to resolve on error exactly as it resolves on load, and it kept the
    failed Image in frameCache - so one refused rendition became a broken
    frame that stayed broken for the life of the page, the same
    "refused once, broken for ever" shape df1aa33 took out of the gloves.
    Now: one retry 700ms later, the entry dropped from the cache so a later
    press asks again rather than replaying the failure, and if the SIZED
    rendition still will not come the frame falls back to the original URL
    the markup carries - a different file, which is usually there. A broken
    frame is the one thing the stage must never show.
  */
  var FRAME_TRIES = 2;
  var FRAME_RETRY_MS = 700;
  var frameCache = {};
  var frameCacheOrder = [];
  var isClosing = false;
  var closeTimer = null;
  var focusableSelector = 'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  function createModal() {
    var node = document.createElement('div');
    node.className = 'longoni-lightbox';
    node.hidden = true;
    node.innerHTML = ''
      + '<div class="longoni-lightbox__backdrop" data-longoni-lightbox-close></div>'
      + '<div class="longoni-lightbox__dialog" role="dialog" aria-modal="true" aria-label="Longoni product image viewer">'
      + '<button class="longoni-lightbox__close" type="button" aria-label="Close image viewer" data-longoni-lightbox-close>'
      + '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M18 6 6 18M6 6l12 12"/></svg>'
      + '</button>'
      + '<div class="longoni-lightbox__media">'
      + '<button class="longoni-lightbox__previous" type="button" aria-label="Previous image" data-longoni-lightbox-previous>'
      + '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="m15 18-6-6 6-6"/></svg>'
      + '</button>'
      + '<div class="longoni-lightbox__image-stage">'
      + '<div class="longoni-lightbox__image-viewport">'
      + '<img class="longoni-lightbox__image" alt="" draggable="false">'
      // A video frame is watched, not magnified: it takes the stage in place
      // of the <img>, with the media's own poster, its controls and nothing
      // preloaded until the viewer reaches it (2026-10-17).
      + '<video class="longoni-lightbox__video" playsinline controls preload="none" style="display:none"></video>'
      + '</div>'
      + '<div class="longoni-lightbox__stagebar">'
      + '<div class="longoni-lightbox__dots" data-longoni-dots hidden></div>'
      + '<div class="longoni-lightbox__zoomcapsule" data-longoni-zoomcapsule>'
      + '<button class="longoni-lightbox__zoomreset" type="button" aria-label="Reset zoom" data-longoni-zoomreset>1x</button>'
      + '<button class="longoni-lightbox__zoomstep" type="button" aria-label="Zoom out" data-longoni-zoomout>'
      + '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle cx="10.5" cy="10.5" r="6.5"/><path d="m16 16 5 5"/><path d="M7.5 10.5h6"/></svg>'
      + '</button>'
      + '<input class="longoni-lightbox__zoomrange" type="range" min="1" max="3" step="0.05" value="1" aria-label="Zoom level" data-longoni-zoomrange>'
      + '<button class="longoni-lightbox__zoomstep" type="button" aria-label="Zoom in" data-longoni-zoomin>'
      + '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle cx="10.5" cy="10.5" r="6.5"/><path d="m16 16 5 5"/><path d="M10.5 7.5v6M7.5 10.5h6"/></svg>'
      + '</button>'
      + '</div>'
      + '</div>'
      + '</div>'
      + '<button class="longoni-lightbox__next" type="button" aria-label="Next image" data-longoni-lightbox-next>'
      + '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="m9 6 6 6-6 6"/></svg>'
      + '</button>'
      + '</div>'
      + '<div class="longoni-lightbox__details">'
      + '<a class="longoni-lightbox__title" href="#" aria-label="Open full product page"></a>'
      + '<div class="longoni-lightbox__price"></div>'
      + '<div class="longoni-lightbox__thumbnails" role="list" aria-label="Product options"></div>'
      + '<div class="longoni-lightbox__actions">'
      + '<button class="longoni-lightbox__desktop-add longoni-lightbox__cart-control product-card-atc" type="button" data-success-text="ADDED TO CART" aria-label="Add current Longoni product to cart">'
      + '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/><line x1="12" y1="11" x2="12" y2="17"/><line x1="9" y1="14" x2="15" y2="14"/></svg>'
      + '<span class="longoni-lightbox__add-label" data-pca-label>ADD TO CART</span>'
      + '</button>'

      + '</div>'
      + '<p class="longoni-lightbox__error" hidden></p>'
      + '</div>'
      + '</div>';

    node.addEventListener('click', function(event) {
      var target = event.target && event.target.closest ? event.target : null;
      if (didPan) {
        event.preventDefault();
        event.stopPropagation();
        didPan = false;
        return;
      }
      if (target && target.closest('[data-longoni-lightbox-close]')) {
        event.stopPropagation();
        closeModal();
      } else if (target && target.closest('[data-longoni-lightbox-previous]')) {
        // The controls own their taps outright: nothing about an arrow
        // press may travel on towards the photo's zoom machinery.
        event.preventDefault();
        event.stopPropagation();
        showAngle(angleIndex - 1);
      } else if (target && target.closest('[data-longoni-lightbox-next]')) {
        event.preventDefault();
        event.stopPropagation();
        showAngle(angleIndex + 1);
      } else if (target && target.closest('[data-longoni-angle-index]')) {
        showAngle(parseInt(target.closest('[data-longoni-angle-index]').getAttribute('data-longoni-angle-index'), 10));
      } else if (target && target.closest('[data-longoni-thumbnail-index]')) {
        showImage(parseInt(target.closest('[data-longoni-thumbnail-index]').getAttribute('data-longoni-thumbnail-index'), 10));
      } else if (target && target.closest('[data-longoni-zoomreset]')) {
        event.stopPropagation();
        resetZoom();
      } else if (target && target.closest('[data-longoni-zoomout]')) {
        event.stopPropagation();
        setZoom(zoomLevel - 0.5);
      } else if (target && target.closest('[data-longoni-zoomin]')) {
        event.stopPropagation();
        setZoom(zoomLevel + 0.5);
      } else if (target && target.closest('[data-longoni-zoomcapsule]')) {
        event.stopPropagation();
      }
    });

    // The capsule swallows its touches whole, so a tap or a slider drag
    // can never read as the stage's swipe gesture underneath it.
    var capsule = node.querySelector('[data-longoni-zoomcapsule]');
    if (capsule) {
      capsule.addEventListener('touchstart', function(event) { event.stopPropagation(); }, { passive: true });
      capsule.addEventListener('touchend', function(event) { event.stopPropagation(); }, { passive: true });
    }

    var stage = node.querySelector('.longoni-lightbox__image-stage');
    var viewport = node.querySelector('.longoni-lightbox__image-viewport');

    stage.addEventListener('touchstart', function(event) {
      if (zoomLevel > MIN_ZOOM) return;
      touchStartX = event.touches && event.touches.length ? event.touches[0].clientX : null;
    }, { passive: true });

    stage.addEventListener('touchend', function(event) {
      if (zoomLevel > MIN_ZOOM) return;
      if (touchStartX === null || !event.changedTouches || !event.changedTouches.length) return;
      var delta = event.changedTouches[0].clientX - touchStartX;
      touchStartX = null;
      if (Math.abs(delta) < 42) return;
      showAngle(delta > 0 ? angleIndex - 1 : angleIndex + 1);
    }, { passive: true });

    viewport.addEventListener('dblclick', function(event) {
      event.preventDefault();
      if (zoomLevel > MIN_ZOOM) {
        resetZoom();
      } else {
        setZoom(BUTTON_ZOOM, event.clientX, event.clientY);
      }
    });

    // The wheel drives the zoom from 1x up, not only once the image is
    // already magnified and not only with ctrl held. The page behind is
    // scroll-locked while the viewer is open, so nothing is lost by
    // claiming the gesture here.
    viewport.addEventListener('wheel', function(event) {
      event.preventDefault();
      var nextZoom = zoomLevel + (event.deltaY < 0 ? 0.2 : -0.2);
      setZoom(nextZoom, event.clientX, event.clientY, true);
    }, { passive: false });

    // The slider zooms about the centre of the frame - there is no pointer
    // position to zoom around when the gesture happens off to the side.
    var range = node.querySelector('[data-longoni-zoomrange]');
    if (range) {
      range.addEventListener('input', function() {
        setZoom(parseFloat(range.value));
      });
      // Keep the arrows and swipes from reacting to keyboard use of the slider.
      range.addEventListener('keydown', function(event) {
        event.stopPropagation();
      });
    }

    viewport.addEventListener('pointerdown', handlePointerDown);
    viewport.addEventListener('pointermove', handlePointerMove);
    viewport.addEventListener('pointerup', handlePointerEnd);
    viewport.addEventListener('pointercancel', handlePointerEnd);
    viewport.addEventListener('lostpointercapture', handlePointerEnd);
    viewport.addEventListener('dragstart', function(event) {
      event.preventDefault();
    });

    document.body.appendChild(node);
    return node;
  }

  function parseProduct(item) {
    var raw = item.getAttribute('data-longoni-product');
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (error) {
      return null;
    }
  }

  /*
    BUYABLE IS ONE RULE, AND IT COMES FROM THE DATA EVERY ROW ALREADY SHIPS.

    data-longoni-product carries variants[] with an available flag per
    variant - the McDermott tiles, the case tiles and the rubber row all
    render it from the same capture - so "can this be bought" is answered
    here once, for every row, without a handle or a row name in the code.

    No variants means no answer, and no answer is NOT sold out: a gallery
    item that fell back to its attributes carries an empty array, and
    greying its button would invent a stock state out of missing data.
  */
  function isBuyable(product) {
    if (!product || !product.variants || !product.variants.length) return true;
    for (var i = 0; i < product.variants.length; i++) {
      if (product.variants[i].available) return true;
    }
    return false;
  }

  function getItems(gallery) {
    return Array.prototype.slice.call(gallery.querySelectorAll('.longoni-promo-strip__gallery-item')).map(function(item) {
      var title = item.getAttribute('data-longoni-title') || item.getAttribute('title') || 'Longoni product image';
      var product = parseProduct(item) || {
        title: title,
        handle: item.getAttribute('data-longoni-handle') || '',
        url: item.getAttribute('data-longoni-url') || item.getAttribute('href'),
        variants: []
      };
      var src = item.getAttribute('data-mfp-src') || item.getAttribute('href');
      var images = null;
      try {
        images = JSON.parse(item.getAttribute('data-longoni-images') || 'null');
      } catch (error) {
        images = null;
      }
      if (!images || !images.length) images = src ? [src] : [];
      return {
        src: src,
        images: images,
        label: item.getAttribute('data-longoni-label') || title,
        tileSrc: item.getAttribute('data-longoni-tile-src') || '',
        thumb: item.getAttribute('data-longoni-thumb') || item.getAttribute('data-mfp-src') || item.getAttribute('href'),
        alt: title,
        title: title,
        price: item.getAttribute('data-longoni-price') || '',
        url: item.getAttribute('data-longoni-url') || product.url || '#',
        handle: item.getAttribute('data-longoni-handle') || product.handle || '',
        product: product
      };
    }).filter(function(item) {
      return !!item.src;
    });
  }

  function renderThumbnails() {
    var thumbnails = modal.querySelector('.longoni-lightbox__thumbnails');
    thumbnails.innerHTML = '';
    // A single-product gallery (the case tiles) has no variants to offer,
    // so the strip disappears and the panel is title, price and ATC.
    thumbnails.hidden = activeItems.length < 2 || activeSurface === 'product';
    if (thumbnails.hidden) return;
    activeItems.forEach(function(item, index) {
      var variant = document.createElement('div');
      variant.className = 'longoni-lightbox__variant';
      variant.setAttribute('role', 'listitem');

      // The strip greys and strikes an option that cannot be bought, the same
      // way the tile's own swatch does, and leaves it pressable: its
      // photographs are still worth looking at, and pressing it is what puts
      // the panel into the SOLD OUT state that explains itself.
      var buyable = isBuyable(item.product);
      if (!buyable) variant.className += ' is-sold-out';

      var button = document.createElement('button');
      button.className = 'longoni-lightbox__thumbnail' + (buyable ? '' : ' is-sold-out');
      button.type = 'button';
      button.setAttribute('aria-label', (buyable ? 'Select ' : 'Select (sold out) ') + (item.label || item.title));
      button.setAttribute('data-longoni-thumbnail-index', index);

      var image = document.createElement('img');
      image.src = item.thumb;
      image.alt = '';
      image.loading = 'lazy';
      button.appendChild(image);

      var label = document.createElement('span');
      label.className = 'longoni-lightbox__thumbnail-label';
      label.textContent = item.label || item.title;

      variant.appendChild(button);
      variant.appendChild(label);
      thumbnails.appendChild(variant);
    });
  }

  function openModal(galleryId, index, trigger) {
    var gallery = document.getElementById(galleryId);
    if (!gallery) return;

    activeItems = getItems(gallery);
    if (!activeItems.length) return;
    activeStatic = gallery.getAttribute('data-longoni-static') === 'true';
    activeSurface = gallery.getAttribute('data-longoni-surface') || '';
    activeMediaFetch = gallery.getAttribute('data-longoni-media-fetch') === 'true';

    modal = modal || createModal();
    modal.classList.toggle('is-static', activeStatic);
    var openingImage = modal.querySelector('.longoni-lightbox__image');
    if (openingImage) openingImage.classList.remove('is-placeholder');
    window.clearTimeout(closeTimer);
    isClosing = false;
    resetZoom();
    activeIndex = Math.max(0, Math.min(parseInt(index || 0, 10), activeItems.length - 1));
    lastTrigger = trigger || document.activeElement;
    lockedScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
    setViewportUnit();

    document.body.classList.add('longoni-lightbox-open');
    document.body.style.top = '-' + lockedScrollY + 'px';
    modal.hidden = false;
    if (prefersReducedMotion()) {
      modal.classList.add('is-open');
    } else {
      void modal.offsetWidth; // force reflow so the enter transition runs
      modal.classList.add('is-open');
    }
    renderThumbnails();
    /*
      Before the first frame is shown, so the panel is already at its final
      height when the dialog animates in - reserving it after would be a
      resize the viewer can see.
    */
    reserveTitleHeight();
    showImage(activeIndex);
    // After the first frame is on its way, never before: the request must
    // not stand between the press and the picture.
    requestFullMedia(activeItems[activeIndex]);

    window.setTimeout(function() {
      var closeButton = modal.querySelector('.longoni-lightbox__close');
      if (closeButton) closeButton.focus();
    }, 0);
  }

  function closeModal() {
    if (!modal || modal.hidden || isClosing) return;
    // A video goes quiet with the dialog, and the stage is handed back to
    // the <img> for the next open.
    stopVideo();
    cancelUpgrade();
    var closingImage = modal.querySelector('.longoni-lightbox__image');
    if (closingImage) closingImage.style.display = '';
    resetZoom();
    modal.classList.remove('is-open');
    document.body.classList.remove('longoni-lightbox-open');
    document.body.style.top = '';
    window.scrollTo(0, lockedScrollY);
    if (lastTrigger && typeof lastTrigger.focus === 'function') {
      lastTrigger.focus();
    }
    if (prefersReducedMotion()) {
      modal.hidden = true;
      return;
    }
    isClosing = true;
    closeTimer = window.setTimeout(function() {
      modal.hidden = true;
      isClosing = false;
    }, 240);
  }

  function cleanPrice(value) {
    var container = document.createElement('div');
    container.innerHTML = value || '';
    return (container.textContent || container.innerText || '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function updateDetails(item) {
    var title = modal.querySelector('.longoni-lightbox__title');
    var price = modal.querySelector('.longoni-lightbox__price');
    var addButtons = Array.prototype.slice.call(modal.querySelectorAll('.longoni-lightbox__cart-control'));
    var error = modal.querySelector('.longoni-lightbox__error');

    title.textContent = item.title;
    if (activeStatic) {
      // A document, not a product: the title names it and leads nowhere.
      title.removeAttribute('href');
      title.removeAttribute('aria-label');
    } else {
      title.href = item.url || '#';
      title.setAttribute('aria-label', 'Open full product page for ' + item.title);
    }
    price.textContent = cleanPrice(item.price);

    /*
      ADD TO CART becomes a grey, disabled SOLD OUT when the selected option
      has no variant left. Disabled rather than styled-and-inert: the click
      never reaches product-card-atc, so there is no silent no-op to explain,
      and the button says what happened instead of appearing to fail.

      The class is removed on every pass, not only added, because the same
      button serves every option in the strip - switching from a sold-out
      variant back to one in stock has to take the grey off again.
    */
    var buyable = isBuyable(item.product);
    addButtons.forEach(function(addButton) {
      var label = addButton.querySelector('[data-pca-label]');
      addButton.dataset.handle = item.handle || '';
      addButton.__pcaProduct = item.product;
      addButton.disabled = !buyable;
      addButton.classList.remove('pca--loading', 'pca--success');
      addButton.classList.toggle('is-sold-out', !buyable);
      if (buyable) {
        addButton.setAttribute('aria-label', 'Add ' + item.title + ' to cart');
        if (label) label.textContent = 'ADD TO CART';
      } else {
        addButton.setAttribute('aria-label', item.title + ' is sold out');
        if (label) label.textContent = 'SOLD OUT';
      }
    });

    error.hidden = true;
    error.textContent = '';
  }

  /*
    THE DIALOG DOES NOT CHANGE HEIGHT WHEN THE VARIANT CHANGES.

    Reported as the gallery resizing itself to each photo's aspect. It is
    not: measured on the draft at 375 across all five TP25A variants, the
    media box holds 366px, the stage 302, the viewport 279 and the photo
    279x279 in every one of them - the square aspect-ratio on
    .image-viewport (see the note beside it in the stylesheet) already
    pins that, and every frame in this catalogue is a 2048x2048 original
    anyway. The filmstrip row holds 78.9px, price 16.8, actions 44.2.

    What moved was the TITLE, and only by its own line height:

      Butt                          1 line   19.9px   details 201   dialog 567
      w/S2, S20, S30, Luna Nera     2 lines  37.9px   details 219   dialog 585

    18px of title is the whole 18px jump. So the fix is a reserve, not a
    cap: the title keeps the height of the LONGEST name among this
    product's own variants, so switching between them cannot move
    anything below it. It is per-product rather than a flat two-line rule,
    because a flat rule would pad every single-line product in the
    catalogue with a blank line it does not need.

    Measured on the real element, not a clone: swapping its text and
    reading back gives the exact box the browser will use, with the same
    width, font and wrapping. Nothing paints in between - the reads are in
    one synchronous pass and the original text is restored before the
    frame ends.

    THE HEIGHT COMES FROM getComputedStyle, NOT getBoundingClientRect, and
    that is not a style preference. This runs immediately after is-open is
    set, so the dialog is part-way through its enter transform, and a
    client rect is in screen space - it comes back multiplied by whatever
    scale the animation happens to be at. Measured: the two-line title
    read 37.92 through the rect while its real laid-out height was 39.5,
    a factor of exactly 0.96, so the reservation came out 1.5px short and
    left a 1.4px twitch behind - the whole bug in miniature, one order of
    magnitude down. Computed height is layout space and ignores ancestor
    transforms entirely.
  */
  function reserveTitleHeight() {
    if (!modal || modal.hidden) return;
    var dialog = modal.querySelector('.longoni-lightbox__dialog');
    var title = modal.querySelector('.longoni-lightbox__title');
    if (!dialog || !title) return;

    // Cleared first, or every pass would measure the last reservation.
    dialog.style.removeProperty('--longoni-title-reserve');
    if (activeItems.length < 2) return;

    var original = title.textContent;
    var tallest = 0;
    activeItems.forEach(function(item) {
      title.textContent = item.title || '';
      var height = parseFloat(window.getComputedStyle(title).height);
      if (height > tallest) tallest = height;
    });
    title.textContent = original;

    if (tallest > 0) {
      dialog.style.setProperty('--longoni-title-reserve', Math.ceil(tallest) + 'px');
    }
  }

  function updateThumbnails() {
    var activeThumb = null;
    Array.prototype.slice.call(modal.querySelectorAll('.longoni-lightbox__thumbnail')).forEach(function(thumbnail, index) {
      var isActive = index === activeIndex;
      thumbnail.classList.toggle('is-active', isActive);
      if (isActive) {
        thumbnail.setAttribute('aria-current', 'true');
        activeThumb = thumbnail;
      } else {
        thumbnail.removeAttribute('aria-current');
      }
    });
    if (activeThumb && activeThumb.scrollIntoView) {
      activeThumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }

  function currentImages() {
    var item = activeItems[activeIndex];
    if (!item) return [];
    return (item.images && item.images.length) ? item.images : [item.src];
  }

  /*
    THE PRODUCT'S WHOLE MEDIA LIST, READ WHEN THE VIEWER OPENS (2026-10-17,
    the owner's brief: the loupe on the catalogue cards, and the full list
    lazily, "not embedded in every card again").

    A card ships six photographs in data-longoni-images and nothing more -
    26b2117 embedded the whole list in the markup instead and grew the
    collection page by 65,145 characters, which is why it was reverted. So
    the viewer opens on what the card carries, instantly, and the rest of
    the list arrives from /products/<handle>.js while the first frame is on
    the stage: one request per product, cached for the page's life, asked
    for only by a gallery that sets data-longoni-media-fetch.

    A refusal is never cached (the rule the audit of 15.10 set): the entry
    is dropped, so the next open asks again. And nothing is replaced while
    it waits - the six frames stay exactly as they are, dots and all.
  */
  var mediaCache = {};
  var MEDIA_FETCH_TRIES = 2;

  function pickVideoSource(sources) {
    if (!sources || !sources.length) return '';
    var best = '';
    var bestHeight = -1;
    for (var i = 0; i < sources.length; i++) {
      var s = sources[i];
      if (!s || !s.url) continue;
      if (s.format && s.format !== 'mp4') continue;   // mp4 plays everywhere; HLS needs a player
      var h = parseInt(s.height || 0, 10) || 0;
      // the largest file at or under 1080p: the stage is never taller
      if (h <= 1080 && h > bestHeight) { bestHeight = h; best = s.url; }
    }
    if (!best) best = sources[0].url || '';
    return best;
  }

  /*
    THE DEALER'S MARK IS NOT A PRODUCT VIEW - the product page's own rule of
    2026-09-17, applied here so the viewer shows the gallery's list and not a
    longer one: around 180 products carry McDermott's "authorized dealer"
    banner among their photographs, a 2585x868 mark rather than a picture of
    the cue, and sections/product-template.liquid leaves it out by this same
    file name. Where every entry is the mark it is all the product has, and
    the list is kept whole (that template's own fallback).
  */
  var DEALER_MARK = 'authorized-dealer-logo';

  function isDealerMark(src) {
    return String(src || '').toLowerCase().indexOf(DEALER_MARK) > -1;
  }

  function parseMedia(json) {
    var out = { images: [], media: [] };
    var list = (json && json.media) || [];
    var marks = 0;
    for (var g = 0; g < list.length; g++) {
      if (list[g] && list[g].media_type === 'image' && isDealerMark(list[g].src)) marks++;
    }
    var dropMark = marks > 0 && marks < list.length;
    for (var i = 0; i < list.length; i++) {
      var m = list[i] || {};
      if (m.media_type === 'image') {
        if (!m.src) continue;
        if (dropMark && isDealerMark(m.src)) continue;
        out.images.push(m.src);
        out.media.push({ type: 'image', src: m.src });
      } else if (m.media_type === 'video') {
        var url = pickVideoSource(m.sources);
        if (!url) continue;
        var poster = (m.preview_image && m.preview_image.src) || '';
        out.images.push(url);
        out.media.push({ type: 'video', src: url, poster: poster });
      }
      // a 3D model or an external video is not something this stage plays;
      // it is left out rather than drawn as a frame that cannot be shown.
    }
    if (!out.images.length && json && json.images && json.images.length) {
      for (var k = 0; k < json.images.length; k++) {
        var src = json.images[k];
        if (!src) continue;
        if (src.indexOf('//') === 0) src = 'https:' + src;
        out.images.push(src);
        out.media.push({ type: 'image', src: src });
      }
    }
    return out;
  }

  function applyFullMedia(item, parsed) {
    if (!item || !parsed || parsed.images.length <= (item.images ? item.images.length : 0)) {
      if (item) item.mediaExpanded = true;
      return;
    }
    /*
      The frame on show must not change under the viewer. The card's first
      six are the product's first six in the same order in the ordinary
      case, but a card whose photograph is a variant's is not, so the frame
      is found again by its file rather than by its index - the size suffix
      and the cache-busting query taken off both sides before comparing.
    */
    var open = modal && !modal.hidden && activeItems[activeIndex] === item;
    var wanted = open ? (currentImages()[angleIndex] || item.src) : '';
    item.images = parsed.images;
    item.media = parsed.media;
    item.mediaExpanded = true;
    if (!open) return;
    var at = -1;
    if (wanted) {
      var key = fileKey(wanted);
      for (var i = 0; i < parsed.images.length; i++) {
        if (fileKey(parsed.images[i]) === key) { at = i; break; }
      }
    }
    angleIndex = at > -1 ? at : Math.min(angleIndex, parsed.images.length - 1);
    renderAngle();
    preloadAround();
  }

  function fileKey(url) {
    var clean = String(url || '').split('?')[0];
    clean = clean.substring(clean.lastIndexOf('/') + 1);
    return clean.replace(/_(\d+)x(\d+)?(?=\.[a-z0-9]+$)/i, '');
  }

  function requestFullMedia(item, attempt) {
    if (!activeMediaFetch || !item || item.mediaExpanded || !item.handle) return;
    if (!window.fetch) return;
    var handle = item.handle;
    var cached = mediaCache[handle];
    if (cached === 'pending') return;
    if (cached) { applyFullMedia(item, cached); return; }
    mediaCache[handle] = 'pending';
    window.fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { accept: 'application/json' } })
      .then(function(response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.json();
      })
      .then(function(json) {
        var parsed = parseMedia(json);
        if (!parsed.images.length) throw new Error('no media');
        mediaCache[handle] = parsed;
        applyFullMedia(item, parsed);
      })
      .catch(function() {
        delete mediaCache[handle];
        if ((attempt || 0) + 1 >= MEDIA_FETCH_TRIES) return;
        window.setTimeout(function() { requestFullMedia(item, (attempt || 0) + 1); }, 900);
      });
  }

  // What is at this position: a photograph, or a video the list brought in.
  function frameAt(index) {
    var item = activeItems[activeIndex];
    if (!item) return null;
    if (item.media && item.media[index]) return item.media[index];
    var images = currentImages();
    var src = images[index] || item.src;
    return src ? { type: 'image', src: src } : null;
  }

  function stopVideo() {
    if (!modal) return;
    var video = modal.querySelector('.longoni-lightbox__video');
    if (!video) return;
    try { video.pause(); } catch (error) { /* a stage that never played */ }
    video.style.display = 'none';
  }

  function renderAngle() {
    var item = activeItems[activeIndex];
    var images = currentImages();
    var image = modal.querySelector('.longoni-lightbox__image');
    var video = modal.querySelector('.longoni-lightbox__video');
    var capsule = modal.querySelector('[data-longoni-zoomcapsule]');
    var frame = frameAt(angleIndex) || { type: 'image', src: item.src };
    if (frame.type === 'video') {
      // The zoom capsule stands down: a video is watched, and the pan/zoom
      // layer writes transforms on the <img>, which is not on the stage.
      resetZoom();
      if (video) {
        if (video.getAttribute('src') !== frame.src) video.setAttribute('src', frame.src);
        if (frame.poster) video.setAttribute('poster', frame.poster);
        else video.removeAttribute('poster');
        video.style.display = '';
      }
      // display, not [hidden]: the sheet gives the image a display of its
      // own and a class rule outranks the attribute.
      if (image) image.style.display = 'none';
      if (capsule) capsule.hidden = true;
    } else {
      stopVideo();
      if (capsule) capsule.hidden = false;
      if (image) {
        image.style.display = '';
        image.src = frameResolved[frame.src] || frameUrl(frame.src);
        image.alt = item.alt;
      }
    }
    // A static gallery keeps its arrows while it has more than one item.
    var hideArrows = activeStatic ? activeItems.length < 2 : images.length < 2;
    modal.querySelector('.longoni-lightbox__previous').hidden = hideArrows;
    modal.querySelector('.longoni-lightbox__next').hidden = hideArrows;

    var dots = modal.querySelector('[data-longoni-dots]');
    if (dots) updateDots(dots, images, hideArrows);
  }

  /*
    Dot indicators under the photo, one per frame of the current variant,
    on every band. They were the phone's stand-in for a column of 92px
    thumbnails down the left of the desktop dialog; that column went on
    2026-09-04 (its 106px track, its scrollbar and a lazy <img> per frame
    with it) and the photo took the width. The dots are buttons carrying
    data-longoni-angle-index, so the click handler at the top treats them
    exactly as it treated the thumbnails - showAngle(i) - and the arrows,
    the swipe and the keyboard are untouched.
  */
  function updateDots(dots, images, hide) {
    dots.hidden = hide;
    if (hide) return;
    if (dots.childElementCount !== images.length) {
      dots.innerHTML = '';
      for (var i = 0; i < images.length; i++) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'longoni-lightbox__dot';
        dot.setAttribute('data-longoni-angle-index', String(i));
        dot.setAttribute('aria-label', 'Photo ' + (i + 1) + ' of ' + images.length);
        dots.appendChild(dot);
      }
    }
    for (var j = 0; j < dots.children.length; j++) {
      dots.children[j].classList.toggle('is-active', j === angleIndex);
    }
  }

  function showAngle(index) {
    if (!modal || !activeItems.length) return;
    var images = currentImages();
    // A static gallery's arrows, keys and swipe step through its ITEMS:
    // each document is one frame, so a step past it is the next document,
    // and showImage's decode gate carries the swap (see activeStatic).
    if (activeStatic && images.length < 2 && activeItems.length > 1) {
      showImage(activeIndex + (index > angleIndex ? 1 : -1));
      return;
    }
    if (images.length < 2) return;
    var next = (index + images.length) % images.length;
    if (next === angleIndex) return;
    // An angle step supersedes any swap still waiting on decode - a stale
    // apply landing afterwards would yank the view to a frame nobody asked
    // for. Rapid presses each take the token; only the last one lands.
    var token = ++showImageToken;
    var forward = index > angleIndex;
    var item = activeItems[activeIndex];
    // The zoom goes at the press, not when the next frame lands: the picture
    // on show returns to 1x and centred straight away, so the arrow answers
    // immediately and the slide that arrives is never inside someone else's
    // magnification. Carrying a zoom across slides is what the owner asked to
    // be rid of.
    resetZoom();
    // And a sharpening still downloading for the frame being left is
    // dropped here, before the new frame's own fetch starts, so the two
    // never share the connection.
    cancelUpgrade();

    // Nothing on the stage changes until the frame is fetched and decoded;
    // the frame on show stays whole in the meantime. Then everything
    // changes together: the src (a decoded file paints on the next frame),
    // the dots, the arrows - and the new frame slides into place from the
    // side it came from, with the slide riding on swapOffset because
    // applyZoomState writes transform inline and an inline transform beats
    // any class.
    // A video frame has nothing to fetch and decode on the stage: the
    // <video> takes its own poster and its own buffering from there.
    var nextFrame = frameAt(next) || { type: 'image', src: images[next] || item.src };
    var prepared = nextFrame.type === 'image'
      ? resolveFrame(nextFrame.src)
      : Promise.resolve(nextFrame.src);
    prepared.then(function() {
      if (token !== showImageToken || !modal || modal.hidden) return;
      angleIndex = next;
      resetZoom();
      var image = modal.querySelector('.longoni-lightbox__image');
      if (!image || prefersReducedMotion()) {
        renderAngle();
        preloadAround();
        scheduleUpgrade(false);
        return;
      }
      swapOffset = forward ? 28 : -28;
      image.classList.add('is-entering');
      applyZoomState(true);                 // the start of the slide, with no transition
      renderAngle();                        // src, dots, arrows: one step
      void image.offsetWidth;               // commit the start state before it moves
      window.requestAnimationFrame(function() {
        if (token !== showImageToken || !modal) return;
        swapOffset = 0;
        image.classList.add('is-sliding');
        image.classList.remove('is-entering');
        applyZoomState(false);              // the transition carries it into place
        window.setTimeout(function() { image.classList.remove('is-sliding'); }, 220);
      });
      preloadAround();
      // Last of all, and only after the slide has its frame and the
      // neighbours have theirs: the frame on show is sharpened.
      scheduleUpgrade(false);
    });
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function showImage(index) {
    if (!modal || !activeItems.length) return;
    cancelUpgrade();
    activeIndex = (index + activeItems.length) % activeItems.length;
    angleIndex = 0;

    var image = modal.querySelector('.longoni-lightbox__image');
    var item = activeItems[activeIndex];
    var images = currentImages();
    var token = ++showImageToken;
    // The target is resolved and decoded first (resolveFrame); the gate below then finds it complete.
    resolveFrame(images[0] || item.src).then(function(targetSrc) {
    if (token !== showImageToken || !modal || modal.hidden) return;

    // Decode first, then swap everything in one synchronous pass with the
    // transition suppressed. Writing src before the file is ready is what
    // flickered on phones: iOS Safari blanks an <img> the moment its src
    // changes, so on a slow load the viewer flashed empty (and whatever
    // frames rapid taps left mid-flight) before the target arrived.
    // Desktop engines keep the old frame while loading, which is why the
    // bug never showed there. The token keeps rapid taps honest: only the
    // latest selection may apply, older decodes are discarded.
    var apply = function() {
      if (token !== showImageToken || !modal || modal.hidden) return;
      if (image) image.classList.add('is-dragging');
      resetZoom();
      renderAngle();
      if (image) image.classList.remove('is-placeholder');
      updateDetails(activeItems[activeIndex]);
      updateThumbnails();
      if (image) {
        void image.offsetWidth;
        image.classList.remove('is-dragging');
      }
      preloadAround();
      scheduleUpgrade(false);
      // Another option's frames: its own list is read the same way, once.
      requestFullMedia(activeItems[activeIndex]);
    };

    if (image && targetSrc && image.getAttribute('src') !== targetSrc) {
      // Whichever readiness signal lands first wins: complete for cached
      // frames, load/error for the network, decode() where it works. The
      // timer is not the mechanism - it is a relief valve for environments
      // where a deferred decode never settles, so the viewer can never be
      // starved into showing nothing at all.
      var applied = false;
      var applyOnce = function() {
        if (applied) return;
        applied = true;
        apply();
      };
      // A static gallery stands the item's small thumbnail in the frame,
      // blurred, while the full file decodes - decoded itself first, so
      // the frame never holds a file the browser has not finished with.
      var placeholderSrc = activeStatic && item.thumb && item.thumb !== targetSrc ? item.thumb : '';
      if (placeholderSrc) {
        var placed = false;
        var placeOnce = function() {
          if (placed || applied || token !== showImageToken || !modal || modal.hidden) return;
          placed = true;
          image.classList.add('is-placeholder');
          image.src = placeholderSrc;
        };
        var placeholder = new Image();
        placeholder.onload = placeOnce;
        placeholder.src = placeholderSrc;
        if (placeholder.complete) placeOnce();
      }
      var preload = new Image();
      preload.onload = applyOnce;
      preload.onerror = applyOnce;
      preload.src = targetSrc;
      if (preload.complete) {
        applyOnce();
      } else {
        if (preload.decode) preload.decode().then(applyOnce, function() {});
        // With a placeholder standing in the frame the valve has nothing
        // to rescue, and at 1200ms it wrote a 1MB original to the stage
        // while it was still downloading (measured at 3 Mbps: complete
        // false from 1230ms on). So it waits out a stalled request only.
        window.setTimeout(applyOnce, placeholderSrc ? 20000 : 1200);
      }
      return;
    }
    apply();
    });
  }

  /*
    A frame at the size the stage draws. Every gallery in this theme hands
    the viewer 2048px originals - the tiles as the legacy "_2048x2048.jpg"
    name, the cards and the product page as "?width=2048" - and Shopify's
    CDN resizes either form on request, never upwards. The need is the
    rendered fit of the photograph (contained in the viewport at the
    frame's own aspect, square when it is not yet known - the shop's
    canvases are square) times devicePixelRatio, rounded up the ladder:
    1250 -> 1400 at 1440x900 on a 2x screen, 1038 -> 1200 on a 430 phone at
    3x. A URL that carries no size at all is asked for the width.
  */
  function sizedUrl(url, px) {
    if (!url) return url;
    if (/_(\d+)x(\d+)(\.[a-z0-9]+)(\?|$)/i.test(url)) return url.replace(/_(\d+)x(\d+)(\.[a-z0-9]+)(\?|$)/i, '_' + px + 'x' + px + '$3$4');
    if (/[?&]width=\d+/.test(url)) return url.replace(/([?&])width=\d+/, '$1width=' + px);
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + px;
  }

  function stageNeedPx(aspect) {
    var viewport = modal && modal.querySelector('.longoni-lightbox__image-viewport');
    var vw = viewport ? viewport.clientWidth : 0;
    var vh = viewport ? viewport.clientHeight : 0;
    if (!vw || !vh) return SIZE_LADDER[SIZE_LADDER.length - 1];
    aspect = aspect || 1;
    var fitW = Math.min(vw, vh * aspect);
    var fitH = Math.min(vh, vw / aspect);
    var need = Math.ceil(Math.max(fitW, fitH) * (window.devicePixelRatio || 1));
    for (var i = 0; i < SIZE_LADDER.length; i++) {
      if (SIZE_LADDER[i] >= need) return SIZE_LADDER[i];
    }
    return SIZE_LADDER[SIZE_LADDER.length - 1];
  }

  function frameUrl(url, aspect) {
    return sizedUrl(url, stageNeedPx(aspect));
  }

  function sizeOf(url) {
    var m = /_(\d+)x\d+\.[a-z0-9]+(\?|$)/i.exec(url || '') || /[?&]width=(\d+)/.exec(url || '');
    return m ? parseInt(m[1], 10) : 0;
  }

  // The frame's URL once it is known what the file is: fetched square-sized
  // first (the shop's canvases are square), and where it turns out wider
  // than tall and the stage draws it wider than that size, the next size up
  // is fetched as well and handed back - decoded, like everything that
  // reaches the stage. The result is kept per source URL so renderAngle
  // writes the same file the gate decoded.
  var frameResolved = {};
  function resolveFrame(url) {
    if (!url) return Promise.resolve(url);
    if (frameResolved[url]) return warm(frameResolved[url]).then(function(ok) { return ok ? frameResolved[url] : url; });
    var first = frameUrl(url, 1);
    return warm(first).then(function(ok) {
      /*
        The sized rendition was refused twice. The ORIGINAL the markup
        carries is a different file and is usually there, so that is what the
        stage gets - rather than a broken frame, and rather than an arrow
        that does nothing. It is remembered, so the retry is not repeated on
        every press.
      */
      if (!ok) { frameResolved[url] = url; return url; }
      var entry = frameCache[first];
      var img = entry && entry.img;
      var chosen = first;
      if (img && img.naturalWidth && img.naturalHeight) {
        var need = stageNeedPx(img.naturalWidth / img.naturalHeight);
        if (need > sizeOf(first)) chosen = sizedUrl(url, need);
      }
      frameResolved[url] = chosen;
      if (chosen === first) return chosen;
      return warm(chosen).then(function(upgraded) {
        if (upgraded) return chosen;
        frameResolved[url] = first;   // the bigger one would not come; the one we have is decoded
        return first;
      });
    });
  }

  // Fetch and decode a frame once; the Image is kept so the decoded bitmap
  // stays warm for a prev/next that comes back to it. A small cache: the
  // oldest entries go when it passes FRAME_CACHE_MAX.
  function warm(url, attempt) {
    if (!url) return Promise.resolve(false);
    if (frameCache[url]) return frameCache[url].ready;
    var img = new Image();
    var ready = new Promise(function(resolve) {
      var ok = function() { resolve(true); };
      var decoded = function() {
        if (img.decode) img.decode().then(ok, ok); else ok();
      };
      var failed = function() {
        // the entry goes, so nothing is ever served a cached failure
        delete frameCache[url];
        var at = frameCacheOrder.indexOf(url);
        if (at !== -1) frameCacheOrder.splice(at, 1);
        if ((attempt || 0) + 1 >= FRAME_TRIES) { resolve(false); return; }
        window.setTimeout(function() {
          warm(url, (attempt || 0) + 1).then(resolve);
        }, FRAME_RETRY_MS);
      };
      img.onload = decoded;
      img.onerror = failed;
      img.src = url;
      if (img.complete && img.naturalWidth) decoded();
    });
    frameCache[url] = { img: img, ready: ready };
    frameCacheOrder.push(url);
    while (frameCacheOrder.length > FRAME_CACHE_MAX) {
      delete frameCache[frameCacheOrder.shift()];
    }
    return ready;
  }

  // The two frames either side of the one on show, and the first frame of
  // the option either side, warmed so the next press finds its file decoded.
  // It hands back when all of them are settled, because the sharpening of
  // the frame on show waits for exactly that (scheduleUpgrade).
  var preloadSettled = null;
  function preloadAround() {
    if (!modal || modal.hidden) return Promise.resolve();
    var waiting = [];
    var images = currentImages();
    var n = images.length;
    if (n > 1) {
      [1, -1, 2, -2].forEach(function(offset) {
        if (Math.abs(offset) >= n) return;
        var at = (angleIndex + offset + n) % n;
        var frame = frameAt(at);
        // A video frame is not warmed: there is no decoded bitmap to keep,
        // and fetching it would be megabytes nobody asked for.
        if (frame && frame.type !== 'image') return;
        waiting.push(resolveFrame(images[at]));
      });
    }
    if (activeItems.length > 1) {
      [1, -1].forEach(function(offset) {
        var neighbour = activeItems[(activeIndex + offset + activeItems.length) % activeItems.length];
        if (!neighbour) return;
        waiting.push(resolveFrame((neighbour.images && neighbour.images.length) ? neighbour.images[0] : neighbour.src));
      });
    }
    preloadSettled = Promise.all(waiting);
    return preloadSettled;
  }

  /*
    THE FRAME ON SHOW IS SHARPENED AFTER IT IS PAINTED, AND ONLY IT
    (2026-10-19, the owner's brief "loupe image quality is bad; slide speed
    must NOT regress"). Measured on the branch before it, on the Lucky L1
    tile: the stage holds whatever rung the slide fetched - 1400px for the
    1281 device px it draws at 1440 on a 2x screen, 1200px for 1038 on a 430
    phone at 3x - so a zoom to 3x stretched that over 3843 / 3114 device px
    until the old 1.3x threshold swapped in the markup's 2048 original, and
    everything below 1.3x was never sharpened at all. The shop's masters ARE
    2048 (every homepage row read: 2048, ball sets 2200) and the CDN never
    serves past a master, so no larger file exists anywhere - all the
    headroom there is lies between 1x and 2x, where the stage was holding
    1400 and 2048 was there for the asking.
    What this does NOT touch is the slide: showAngle still fetches the 1x
    rung, decodes it and swaps it in one step, which is where the speed
    lives. Once that frame is PAINTED, the one frame on show is asked for a
    bigger rung - its drawn width times devicePixelRatio times the zoom, or
    1.5x of it while un-zoomed, up the ladder, never past the size the
    markup declares the original to be - fetched and decoded off-stage, and
    written to the same <img>, so the inline transform rides through and
    zoom, pan and the drawn box are untouched: the picture simply sharpens.
    Never a neighbour - preloadAround still warms those at the small size -
    and an arrow press drops a sharpening that is still downloading, so the
    bandwidth belongs to the slide that was asked for.
  */
  var UPGRADE_LADDER = [1400, 1600, 2048, 2560, 3072];
  var UPGRADE_DELAY_MS = 250;        // the slide animation is 160ms; the neighbours go first
  var UPGRADE_IDLE_HEADROOM = 1.5;   // un-zoomed, so the first pinch is already sharp
  var UPGRADE_CACHE_MAX = 2;         // its own cache: the slide's 14 decoded frames stay whole
  var upgradeToken = 0;
  var upgradeTimer = null;
  var upgradePending = null;
  var upgradeCache = {};
  var upgradeOrder = [];
  var servedCap = {};                // source url -> what the CDN gave when asked for more

  // The width this frame would need to be drawn from, at this zoom, rounded
  // up the ladder. offsetWidth is the layout width - the box the photograph
  // occupies before the zoom's transform - so this is exact for a portrait
  // frame as well as a square one.
  function upgradeAsk(image, zoom) {
    // The UNZOOMED fit: since 2026-10-20 the layout box carries the zoom
    // (settleLayout), so offsetWidth would already include it and the zoom
    // would be counted twice.
    var fit = fitWidth(image);
    if (!fit) return 0;
    var need = Math.ceil(fit * (window.devicePixelRatio || 1) * Math.max(zoom || 1, 1));
    for (var i = 0; i < UPGRADE_LADDER.length; i++) {
      if (UPGRADE_LADDER[i] >= need) return UPGRADE_LADDER[i];
    }
    return UPGRADE_LADDER[UPGRADE_LADDER.length - 1];
  }

  // Fetched and decoded away from the stage, in a cache of its own, with no
  // retry: a sharpening that will not come leaves the frame on show exactly
  // as it is, which is the whole safety of it.
  function warmUpgrade(url, ask, sourceUrl) {
    if (upgradeCache[url]) return upgradeCache[url].ready;
    var img = new Image();
    var ready = new Promise(function(resolve) {
      var settled = false;
      var done = function(ok) {
        if (settled) return;
        settled = true;
        if (upgradePending === img) upgradePending = null;
        resolve(ok);
      };
      var loaded = function() {
        // The CDN never upscales: a response narrower than the ask IS the
        // master, and nothing wider will ever come for this photograph.
        if (img.naturalWidth && img.naturalWidth < ask) servedCap[sourceUrl] = img.naturalWidth;
        if (img.decode) img.decode().then(function() { done(true); }, function() { done(true); });
        else done(true);
      };
      img.onload = loaded;
      img.onerror = function() {
        delete upgradeCache[url];
        var at = upgradeOrder.indexOf(url);
        if (at !== -1) upgradeOrder.splice(at, 1);
        done(false);
      };
      img.src = url;
      if (img.complete && img.naturalWidth) loaded();
    });
    upgradePending = img;
    upgradeCache[url] = { img: img, ready: ready };
    upgradeOrder.push(url);
    while (upgradeOrder.length > UPGRADE_CACHE_MAX) {
      delete upgradeCache[upgradeOrder.shift()];
    }
    return ready;
  }

  // An arrow, a tap on a dot, a new option or a close: whatever is being
  // fetched for the old frame is dropped on the spot. An <img> whose src is
  // cleared abandons its request, which is the point - the next slide's
  // small frame is what the connection is for.
  function cancelUpgrade() {
    upgradeToken++;
    if (upgradeTimer) {
      window.clearTimeout(upgradeTimer);
      upgradeTimer = null;
    }
    if (upgradePending) {
      try { upgradePending.src = ''; } catch (error) { /* an Image the engine cannot abort */ }
      upgradePending = null;
    }
  }

  /*
    THE SHARPENING QUEUES BEHIND THE SLIDE'S OWN FILES, IT NEVER RACES THEM
    (measured, and it is what the first build got wrong). Started 250ms after
    the swap, the 2048 rung took the connection and the decoder away from the
    neighbours preloadAround had just asked for, and a press at the next
    second found them neither downloaded nor decoded: the painted time at
    1440 went from a 32ms median to 128 (and 237 at worst) with nothing on
    the slide path changed. So the sharpening now waits for preloadAround to
    settle - the ±1, ±2 frames and the neighbouring options' first frames,
    all at the small size - and only then takes its idle gap. A press in the
    meantime cancels it outright. A zoom still goes immediately: the viewer
    is looking at that frame and has asked for detail.
  */
  function scheduleUpgrade(immediate) {
    cancelUpgrade();
    var token = upgradeToken;
    var run = function() {
      upgradeTimer = null;
      if (token === upgradeToken) upgradeFrame(token);
    };
    if (immediate) { run(); return; }
    Promise.resolve(preloadSettled).then(function() {
      if (token !== upgradeToken) return;
      upgradeTimer = window.setTimeout(run, UPGRADE_DELAY_MS);
    }, function() {
      if (token !== upgradeToken) return;
      upgradeTimer = window.setTimeout(run, UPGRADE_DELAY_MS);
    });
  }

  function upgradeFrame(token) {
    if (!modal || modal.hidden) return;
    var image = modal.querySelector('.longoni-lightbox__image');
    var frame = frameAt(angleIndex);
    if (!image || !frame || frame.type !== 'image' || !frame.src) return;
    if (!image.naturalWidth) return;
    // A metered or slow connection is sharpened only when the viewer asks
    // for it by zooming; at rest it keeps the frame the slide fetched.
    var link = navigator.connection;
    if (link && (link.saveData || /2g$/.test(link.effectiveType || '')) && zoomLevel <= MIN_ZOOM) return;
    var ask = upgradeAsk(image, zoomLevel > MIN_ZOOM ? zoomLevel : UPGRADE_IDLE_HEADROOM);
    if (!ask) return;
    // Never past the original the markup names, and never past what this
    // photograph's master turned out to be - either way the bytes would be
    // the same file under another name.
    var declared = sizeOf(frame.src);
    if (declared && ask > declared) ask = declared;
    if (servedCap[frame.src] && ask > servedCap[frame.src]) ask = servedCap[frame.src];
    if (ask <= image.naturalWidth) return;
    var url = sizedUrl(frame.src, ask);
    if (!url || url === image.getAttribute('src')) return;
    var at = activeIndex + ':' + angleIndex;
    warmUpgrade(url, ask, frame.src).then(function(ok) {
      if (!ok || token !== upgradeToken || !modal || modal.hidden) return;
      if (activeIndex + ':' + angleIndex !== at) return;
      var current = modal.querySelector('.longoni-lightbox__image');
      if (!current || current !== image) return;
      // A master smaller than the ask comes back at its own width under the
      // bigger name: the same pixels, so the stage is left alone rather than
      // handed an identical file (an <img> whose src changes is a frame iOS
      // Safari can blank, and for nothing).
      var got = upgradeCache[url] && upgradeCache[url].img;
      if (!got || !got.naturalWidth || got.naturalWidth <= image.naturalWidth) return;
      // The same element, the same box, the same inline transform: the
      // decoded file simply replaces the one being drawn from.
      image.src = url;
    });
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function getZoomElements() {
    if (!modal) return null;
    return {
      viewport: modal.querySelector('.longoni-lightbox__image-viewport'),
      image: modal.querySelector('.longoni-lightbox__image'),
      capsule: modal.querySelector('[data-longoni-zoomcapsule]'),
      reset: modal.querySelector('[data-longoni-zoomreset]'),
      range: modal.querySelector('[data-longoni-zoomrange]')
    };
  }

  /*
    THE ZOOM IS THE IMAGE'S LAYOUT BOX, NOT A TRANSFORM ON ITS LAYER
    (2026-10-20, the owner's brief after checking on a real iPhone: waiting
    does NOT sharpen a zoomed frame, it stays soft indefinitely - so the
    sharper file is already on the stage and the browser is stretching a 1x
    raster. That is the behaviour will-change: transform was suspected of,
    and dropping the hint on 19.10 did not cure it, because the inline
    translate3d promotes the layer anyway and WebKit keeps a promoted
    layer's backing store at the scale it was rasterised at.)
    So the scale goes through LAYOUT: the <img> is given an explicit width
    and height - its unzoomed fit times the zoom - and the transform is left
    carrying the pan alone. A box 1922px wide is painted from 1922px of
    source because that is the element's own size; no compositor
    magnification is in the path, and only the visible tiles are rasterised,
    which is why this costs less memory than the whole box suggests.
    The GESTURE still rides the transform, which is what keeps a pinch and a
    step smooth: scale(zoomLevel / layoutZoom) animates on the compositor
    exactly as it used to. Then, after the last pinch move or past the
    step's own 240ms transition, the layout takes the size and the transform
    gives up its scale - one step, transition suppressed, identical
    geometry either side of it, so nothing moves and only the raster
    changes. A slide clears the box synchronously (resetZoom), because the
    next frame must never land inside the old frame's zoomed box.
  */
  var layoutZoom = MIN_ZOOM;
  var baseW = 0;
  var baseH = 0;
  var settleTimer = null;
  var SETTLE_GESTURE_MS = 120;   // after the last pinch or pan event
  var SETTLE_STEP_MS = 260;      // past the 240ms transform transition

  // The unzoomed fit, read only while the box is still the natural one.
  // Every slide resets the zoom, so it is re-read per frame and a portrait
  // frame cannot inherit a landscape one's numbers.
  function measureBase(image) {
    // An inline width means the box is carrying a zoom, and with the fit
    // caps lifted beside it the element's own fit is not readable there -
    // it reports its file's 2048px. One signal for both, because the width
    // and the is-sized class always travel together.
    if (!image || image.style.width) return;
    if (image.offsetWidth && image.offsetHeight) {
      baseW = image.offsetWidth;
      baseH = image.offsetHeight;
    }
  }

  // The fit the zoom multiplies: the stored base while the layout carries
  // the zoom, the element's own box while it does not.
  function fitWidth(image) {
    if (layoutZoom > MIN_ZOOM && baseW) return baseW;
    return image ? image.offsetWidth : 0;
  }

  function writeTransform(image) {
    var factor = layoutZoom > 0 ? (zoomLevel / layoutZoom) : zoomLevel;
    var transform = 'translate3d(' + (panX + swapOffset) + 'px, ' + panY + 'px, 0)';
    if (Math.abs(factor - 1) > 0.001) transform += ' scale(' + factor + ')';
    image.style.transform = transform;
  }

  function cancelSettle() {
    if (settleTimer) {
      window.clearTimeout(settleTimer);
      settleTimer = null;
    }
  }

  function scheduleSettle(delay) {
    cancelSettle();
    settleTimer = window.setTimeout(settleLayout, delay);
  }

  function settleLayout() {
    settleTimer = null;
    if (!modal || modal.hidden) return;
    var elements = getZoomElements();
    if (!elements || !elements.image) return;
    if (layoutZoom === zoomLevel) return;
    var image = elements.image;
    measureBase(image);
    if (!baseW || !baseH) return;
    var wasDragging = image.classList.contains('is-dragging');
    image.classList.add('is-dragging');        // transition: none for this one step
    if (zoomLevel <= MIN_ZOOM) {
      image.style.width = '';
      image.style.height = '';
      image.classList.remove('is-sized');
    } else {
      image.style.width = (Math.round(baseW * zoomLevel * 100) / 100) + 'px';
      image.style.height = (Math.round(baseH * zoomLevel * 100) / 100) + 'px';
      image.classList.add('is-sized');
    }
    layoutZoom = zoomLevel;
    writeTransform(image);
    void image.offsetWidth;                    // commit before the transition returns
    if (!wasDragging) image.classList.remove('is-dragging');
    // the drawn size has changed, so the rung this frame needs may have too
    scheduleUpgrade(true);
  }

  // The box goes back to its own fit at once - no animation, no settle.
  function clearLayoutZoom() {
    var elements = getZoomElements();
    if (!elements || !elements.image) return;
    elements.image.style.width = '';
    elements.image.style.height = '';
    elements.image.classList.remove('is-sized');
    layoutZoom = MIN_ZOOM;
  }

  function getPanBounds(nextZoom) {
    var elements = getZoomElements();
    if (!elements) return { x: 0, y: 0 };
    var viewportWidth = elements.viewport.clientWidth;
    var viewportHeight = elements.viewport.clientHeight;
    var fit = fitWidth(elements.image);
    var ratio = baseW && baseH ? (baseH / baseW) : (elements.image.offsetHeight / (elements.image.offsetWidth || 1));
    var imageWidth = fit * nextZoom;
    var imageHeight = fit * ratio * nextZoom;
    return {
      x: Math.max(0, (imageWidth - viewportWidth) / 2),
      y: Math.max(0, (imageHeight - viewportHeight) / 2)
    };
  }

  function constrainPan() {
    var bounds = getPanBounds(zoomLevel);
    panX = clamp(panX, -bounds.x, bounds.x);
    panY = clamp(panY, -bounds.y, bounds.y);
  }

  function applyZoomState(isDragging) {
    var elements = getZoomElements();
    if (!elements) return;
    measureBase(elements.image);
    constrainPan();
    writeTransform(elements.image);
    // The crisp raster arrives when the gesture is over, or past the step's
    // own transition - never during either, where it would fight the
    // animation it is riding on.
    if (zoomLevel !== layoutZoom) scheduleSettle(isDragging ? SETTLE_GESTURE_MS : SETTLE_STEP_MS);
    elements.image.classList.toggle('is-zoomed', zoomLevel > MIN_ZOOM);
    elements.image.classList.toggle('is-dragging', !!isDragging);
    elements.viewport.classList.toggle('is-zoomed', zoomLevel > MIN_ZOOM);
    elements.viewport.classList.toggle('is-dragging', !!isDragging);
    // The capsule wakes to full opacity while zoomed, and the 1x reset
    // fades in beside it only once there is something to reset.
    if (elements.capsule) elements.capsule.classList.toggle('is-zoomed', zoomLevel > MIN_ZOOM);
    if (elements.reset) elements.reset.classList.toggle('is-visible', zoomLevel > MIN_ZOOM);

  /*
    THE ARROWS ADVANCE AT ANY ZOOM (2026-10-17, the owner's bug report; it
    REPLACES the rule of 2026-09-29 that disabled them above 1x). That rule is
    what the owner hit: zoom in, press next, and nothing happens - not because
    the pan layer swallowed the press, but because this line set
    button.disabled, and a disabled button fires no click at all. The pan
    gesture never needed it: the image is clipped by its viewport, so a
    magnified photograph does not cover the arrows, and the press lands on the
    button either way. So nothing is disabled here any more, and showAngle
    takes the zoom back to 1x the moment a step is accepted - every slide
    opens as a plain picture, by arrow, by key and by dot alike. The stage's
    SWIPE still stands down above 1x, because there the gesture is the pan.
  */
    // The slider is a readout as much as a control: the wheel, pinch,
    // dblclick and the step buttons all end up here, so whatever moved the
    // zoom, the handle follows. Guarded against writing back into an input
    // the user is currently dragging, which would fight their pointer.
    if (elements.range && document.activeElement !== elements.range) {
      elements.range.value = String(zoomLevel);
    }
  }

  function setZoom(nextZoom, originX, originY, skipTransition) {
    var elements = getZoomElements();
    if (!elements) return;
    var previousZoom = zoomLevel;
    nextZoom = clamp(nextZoom, MIN_ZOOM, MAX_ZOOM);

    if (nextZoom === MIN_ZOOM) {
      zoomLevel = MIN_ZOOM;
      panX = 0;
      panY = 0;
      applyZoomState(false);
      return;
    }

    if (originX !== undefined && originY !== undefined && previousZoom !== nextZoom) {
      var rect = elements.viewport.getBoundingClientRect();
      var offsetX = originX - rect.left - rect.width / 2;
      var offsetY = originY - rect.top - rect.height / 2;
      var ratio = nextZoom / previousZoom;
      panX = panX * ratio - offsetX * (ratio - 1);
      panY = panY * ratio - offsetY * (ratio - 1);
    }

    zoomLevel = nextZoom;
    if (skipTransition) elements.image.classList.add('is-dragging');
    applyZoomState(false);
    if (skipTransition) elements.image.classList.remove('is-dragging');
    // A zoom is the viewer asking to look closer, so the sharper rung is
    // fetched at once instead of after the idle wait.
    scheduleUpgrade(true);
  }

  function resetZoom() {
    zoomLevel = MIN_ZOOM;
    panX = 0;
    panY = 0;
    isPanning = false;
    activePointers = [];
    // Synchronously, and without waiting for a settle: a slide calls this
    // before the next frame is rendered, and that frame must not be laid
    // out inside the box the last one was zoomed to. The zoom RESET button
    // does not come through here - it animates scale back to 1x and the
    // settle clears the box afterwards, exactly as it always looked.
    cancelSettle();
    clearLayoutZoom();
    applyZoomState(false);
  }

  function updatePointer(event) {
    for (var i = 0; i < activePointers.length; i++) {
      if (activePointers[i].id === event.pointerId) {
        activePointers[i].x = event.clientX;
        activePointers[i].y = event.clientY;
        return;
      }
    }
  }

  function removePointer(event) {
    activePointers = activePointers.filter(function(pointer) {
      return pointer.id !== event.pointerId;
    });
  }

  function getPointerDistance() {
    if (activePointers.length < 2) return 0;
    var dx = activePointers[0].x - activePointers[1].x;
    var dy = activePointers[0].y - activePointers[1].y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  function handlePointerDown(event) {
    var elements = getZoomElements();
    if (!elements) return;
    activePointers.push({ id: event.pointerId, x: event.clientX, y: event.clientY });

    if (activePointers.length === 2) {
      pinchStartDistance = getPointerDistance();
      pinchStartZoom = zoomLevel;
      event.preventDefault();
      return;
    }

    if (zoomLevel <= MIN_ZOOM) return;
    event.preventDefault();
    isPanning = true;
    didPan = false;
    panStartX = event.clientX;
    panStartY = event.clientY;
    startPanX = panX;
    startPanY = panY;
    elements.viewport.setPointerCapture(event.pointerId);
    applyZoomState(true);
  }

  function handlePointerMove(event) {
    updatePointer(event);

    if (activePointers.length >= 2 && pinchStartDistance > 0) {
      event.preventDefault();
      var distance = getPointerDistance();
      if (distance > 0) setZoom(pinchStartZoom * (distance / pinchStartDistance), event.clientX, event.clientY, true);
      return;
    }

    if (!isPanning || zoomLevel <= MIN_ZOOM) return;
    event.preventDefault();
    var deltaX = event.clientX - panStartX;
    var deltaY = event.clientY - panStartY;
    if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) didPan = true;
    panX = startPanX + deltaX;
    panY = startPanY + deltaY;
    applyZoomState(true);
  }

  function handlePointerEnd(event) {
    removePointer(event);
    if (activePointers.length < 2) pinchStartDistance = 0;
    if (!isPanning) return;
    isPanning = false;
    applyZoomState(false);
  }

  function trapFocus(event) {
    if (!modal || modal.hidden || event.key !== 'Tab') return;
    if (document.getElementById('pca-picker-overlay')) return;
    var focusable = Array.prototype.slice.call(modal.querySelectorAll(focusableSelector)).filter(function(element) {
      return element.offsetParent !== null && !element.disabled;
    });
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function handleKeydown(event) {
    if (!modal || modal.hidden) return;
    if (document.getElementById('pca-picker-overlay')) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      closeModal();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      showAngle(angleIndex - 1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      showAngle(angleIndex + 1);
    } else {
      trapFocus(event);
    }
  }

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  var bandMeasurements = {};
  var sameLineBand = window.matchMedia && window.matchMedia('(min-width: 1850px)');
  // The phone layout, where the band is anchored in px under the price.
  var phoneBand = window.matchMedia && window.matchMedia('(max-width: 749px)');
  // The reach the phone CSS anchor is cut for - see applyPhoneBandShift.
  var PHONE_BAND_REFERENCE_UP = 20;

  function measureBandExtent(imgEl) {
    // Reads the already-loaded chip <img> (crossorigin="anonymous" in markup) -
    // no image is ever fetched for measurement. Synchronous; caller ensures
    // imgEl.complete. Returns {up, height} in image-size fractions, or null.
    var key = imgEl.currentSrc || imgEl.src;
    if (Object.prototype.hasOwnProperty.call(bandMeasurements, key)) return bandMeasurements[key];
    var m = null;
    try {
      var n = 64;
      var canvas = document.createElement('canvas');
      canvas.width = n;
      canvas.height = n;
      var ctx = canvas.getContext('2d');
      ctx.drawImage(imgEl, 0, 0, n, n);
      var data = ctx.getImageData(0, 0, n, n).data;
      var minDev = 2;
      var maxDev = -2;
      for (var y = 0; y < n; y++) {
        for (var x = 0; x < n; x++) {
          var i = (y * n + x) * 4;
          if ((data[i] + data[i + 1] + data[i + 2]) / 3 >= 245) continue;
          var dev = (x + y - n) / n;
          if (dev < -0.22) continue; // outside the diagonal clip band, mirrors the CSS clip-path
          if (dev < minDev) minDev = dev;
          if (dev > maxDev) maxDev = dev;
        }
      }
      if (maxDev >= minDev) {
        var up = Math.max(0, -minDev) / 1.4142;
        m = { up: up, height: up + Math.max(0, maxDev) / 1.4142 };
      }
    } catch (error) {
      m = null; // tainted canvas or decode failure: no fit data, band renders as before
    }
    bandMeasurements[key] = m;
    return m;
  }

  function bindPricePreview(tile) {
    var priceEl = tile.querySelector('.longoni-promo-strip__price');
    if (!priceEl) return;

    var eyebrowEl = priceEl.querySelector('.longoni-promo-strip__price-eyebrow');
    var figureEl = priceEl.querySelector('.longoni-promo-strip__price-figure');
    var originalParts = {
      eyebrow: eyebrowEl ? eyebrowEl.textContent : '',
      figure: figureEl ? figureEl.textContent : priceEl.textContent,
      figureHtml: figureEl ? figureEl.innerHTML : null
    };
    // The money filter wraps every price in <span class="hidePrice"> - the
    // wholesale app's price-hiding hook. No write may strip it: restores go
    // back through the captured markup, previews rebuild the wrapper.
    var figureKeepsHidePrice = !!(figureEl && figureEl.querySelector('.hidePrice'));
    var fromTemplate = tile.getAttribute('data-longoni-from-template') || '[PRICE]';
    var hoverCapable = !!(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches);
    var items = null;
    var swapTimer = null;
    var originalImage = tile.style.getPropertyValue('--longoni-image');
    var cta = tile.querySelector('.brand-promo__button.bc-buy') || tile.querySelector('.brand-promo__button');
    var soldOutEl = tile.querySelector('[data-longoni-soldout]');
    var originalCtaUrl = cta ? cta.getAttribute('href') : '';
    var imageTimer = null;
    var swapToken = 0;
    var preloaded = false;
    var previewRowsSelector = tile.getAttribute('data-longoni-preview-rows') || '';
    var previewRows = previewRowsSelector ? Array.prototype.slice.call(tile.querySelectorAll(previewRowsSelector)) : [];
    // data-src, not just src: a tile past the fourth in a rail row ships its
    // bands deferred (see assets/bc-tile-lazy.js), so at this point src is
    // null and reading only src would record "no image" as the default. The
    // first hover would then swap the band in and leaving would blank it.
    var defaultPreviewRows = previewRows.map(function(img) {
      if (img.style.visibility === 'hidden') return '';
      return img.getAttribute('src') || img.getAttribute('data-src') || '';
    });
    var defaultActiveThumb = tile.querySelector('.longoni-promo-strip__mini-button--thumb.is-active') || tile.querySelector('.longoni-promo-strip__mini-button--thumb');
    var previewRowsPreloaded = false;
    // A swatch names one source per band row: three on every pool, McDermott
    // and case tile, four on a carom tile, whose two-shaft sets show both
    // shafts. Never fewer than three, so a tile whose selector matches fewer
    // images still preloads and waits on exactly the sources it always did.
    var previewRowCount = Math.max(3, previewRows.length);
    function rowSourcesOf(thumb) {
      var sources = [];
      for (var r = 1; r <= previewRowCount; r++) sources.push(thumb.getAttribute('data-longoni-preview-row-' + r));
      return sources;
    }

    function applyPreviewRows(sources) {
      previewRows.forEach(function(img, index) {
        var source = sources[index] || '';
        img.style.visibility = source ? '' : 'hidden';
        if (source && img.getAttribute('src') !== source) img.setAttribute('src', source);
        /* bc-tile-lazy's loadNow writes the SERVER band over any img that
           still carries data-src, so a state applied on a deferred tile
           before its lazy load fired was replaced by the default. Once a real
           src is in place the deferred one has no job left. */
        if (source) img.removeAttribute('data-src');
      });
    }

    function preloadPreviewRows() {
      if (!previewRows.length || previewRowsPreloaded) return;
      previewRowsPreloaded = true;
      Array.prototype.slice.call(tile.querySelectorAll('[data-longoni-preview-row-1]')).forEach(function(thumb) {
        rowSourcesOf(thumb).forEach(function(source) {
          if (!source) return;
          var image = new Image();
          image.src = source;
          if (image.decode) image.decode().catch(function() {});
        });
      });
    }

    /*
      Warm every variant's bands when the tile comes within 600px of the
      viewport, the same approach the rubber row uses.

      preloadPreviewRows already existed but only ran on the way INTO a
      preview, so the first swatch pressed on a tile paid the whole
      network cost inside the 400ms gate and swapped late however good the
      gate was. Warming on approach means the decode the gate waits for is
      already done, so the swap is immediate.

      Still nothing for a tile the reader never reaches: this is what the
      comment further down insists on, and an observer honours it where
      the old init-time preload did not.
    */
    if (previewRows.length && window.IntersectionObserver) {
      var warmObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          preloadPreviewRows();
          warmObserver.unobserve(entry.target);
        });
      }, { rootMargin: '600px 0px' });
      warmObserver.observe(tile);
    }

    function showPreviewRows(thumb) {
      preloadPreviewRows();
      setPreviewRows(rowSourcesOf(thumb));
    }

    function restorePreviewRows() {
      setPreviewRows(defaultPreviewRows);
    }

    function preloadTileImages() {
      if (preloaded) return;
      preloaded = true;
      getTileItems().forEach(function(item) {
        if (item.tileSrc) {
          var img = new Image();
          img.src = item.tileSrc;
        }
      });
    }

    var bandToken = 0;
    var originalFitImg;

    function clearBandFit() {
      tile.style.removeProperty('--longoni-band-scale');
      tile.style.removeProperty('--longoni-band-ty');
      tile.style.removeProperty('--longoni-band-shift');
    }

    /*
      Phones: the band's top is a fixed px anchor under the price, cut for
      the TALLER of the two photo shapes - a shaft variant shows butt and
      shaft together and reaches about 20px above the band's centre, where
      the single-cue default reaches about 7. Left alone, the default photo
      hangs 13px lower than it needs to, which is the air over the cue.

      So the difference is paid per photo rather than in both states: each
      photo is measured as it swaps in and the band shifts by how far its
      own reach differs from the 20px the CSS anchor assumes. The cue's TOP
      edge then lands in the same place whichever photo is showing, and the
      clearance under the digits is the same in both.

      A transform rather than the top, so it animates on the compositor and
      costs no layout; the transition lives with the rule in the section.
    */
    function applyPhoneBandShift(imgEl) {
      var m = imgEl ? measureBandExtent(imgEl) : null;
      if (!m || typeof m.up !== 'number') {
        tile.style.removeProperty('--longoni-band-shift');
        return;
      }
      // The band's side, read from the CSS rather than restated here: the
      // phone rule sets a different width from the desktop one (71 vs 74
      // per cent) and a copy of the number in this file would silently
      // drift from it. Falls back to the phone value if the pseudo cannot
      // be measured.
      var bandStyle = window.getComputedStyle(tile, '::before');
      var bandSide = bandStyle ? parseFloat(bandStyle.width) : NaN;
      if (!(bandSide > 0)) bandSide = 0.71 * tile.clientWidth;
      var reach = m.up * bandSide;
      tile.style.setProperty('--longoni-band-shift', (reach - PHONE_BAND_REFERENCE_UP).toFixed(1) + 'px');
    }

    function applyBandFit(imgEl) {
      bandToken++;
      var token = bandToken;
      if (phoneBand && phoneBand.matches) {
        tile.style.removeProperty('--longoni-band-scale');
        tile.style.removeProperty('--longoni-band-ty');
        if (!imgEl) { tile.style.removeProperty('--longoni-band-shift'); return; }
        var runPhone = function() {
          if (token !== bandToken) return;
          applyPhoneBandShift(imgEl);
        };
        if (imgEl.complete && imgEl.naturalWidth > 0) runPhone();
        else imgEl.addEventListener('load', runPhone, { once: true });
        return;
      }
      if (!imgEl || !sameLineBand || !sameLineBand.matches) { clearBandFit(); return; }
      var run = function() {
        if (token !== bandToken) return;
        var m = measureBandExtent(imgEl);
        if (!m || !m.height) { clearBandFit(); return; }
        var w = tile.clientWidth;
        var h = tile.clientHeight;
        var a = 0.74 * w;
        var zone = h - 125 - 66; // clearance line to button clearance; mirrors the same-line CSS constants
        var bandH = m.height * a;
        if (zone <= 0 || bandH <= zone) { clearBandFit(); return; }
        var s = zone / bandH;
        var ty = (125 + s * m.up * a) - (125 + 0.06 * w);
        tile.style.setProperty('--longoni-band-scale', s.toFixed(4));
        tile.style.setProperty('--longoni-band-ty', ty.toFixed(1) + 'px');
      };
      if (imgEl.complete && imgEl.naturalWidth > 0) {
        run(); // chip image already decoded: fit is set before the swapped band paints
      } else {
        imgEl.addEventListener('load', run, { once: true });
      }
    }

    function findOriginalFitImg() {
      if (originalFitImg !== undefined) return originalFitImg;
      originalFitImg = null;
      var urlMatch = (originalImage || '').match(/url\(['"]?([^'")]+)/);
      if (!urlMatch) return originalFitImg;
      var tileItems = getTileItems();
      var thumbEls = tile.querySelectorAll('.longoni-promo-strip__mini-button--thumb');
      for (var i = 0; i < thumbEls.length; i++) {
        var idx = parseInt(thumbEls[i].getAttribute('data-longoni-gallery-index') || '-1', 10);
        if (tileItems[idx] && tileItems[idx].tileSrc === urlMatch[1]) {
          originalFitImg = thumbEls[i].querySelector('img');
          break;
        }
      }
      return originalFitImg;
    }

    function setTileImage(value, fitImgEl) {
      if (!value) return;
      window.clearTimeout(imageTimer);
      if (tile.style.getPropertyValue('--longoni-image') === value) {
        tile.classList.remove('is-image-swapping');
        return;
      }
      // The fit is NOT applied here. It used to be, and that is the jump:
      // the shift is a property of the INCOMING photo, but the outgoing one
      // is still on screen for as long as the decode takes - measured on the
      // preview at 390, the shift moved at t=0 and the image only at t=120,
      // so for 120ms the old cue slid to where the new one belongs, over the
      // ::before's own 140ms transform transition. It now travels with the
      // pixels, inside the swap below, while the band is at opacity 0.
      if (prefersReducedMotion()) {
        applyBandFit(fitImgEl);
        tile.style.setProperty('--longoni-image', value);
        return;
      }
      // Decode the target before ever hiding the current frame: the old
      // blind 140ms timer flipped is-image-swapping (opacity:0 on the
      // diamond ::before) on a clock, with no guarantee the new
      // background-image had actually painted by then - on an uncached
      // swap the viewer saw the dark tile through the diamond's clip-path
      // notch for however long the paint actually took. Same
      // decode-then-swap shape as the lightbox's own showImage.
      var token = ++swapToken;
      var urlMatch = value.match(/url\(['"]?([^'")]+)/);
      var targetSrc = urlMatch ? urlMatch[1] : null;
      var startFade = function() {
        if (token !== swapToken) return;
        tile.classList.add('is-image-swapping');
        imageTimer = window.setTimeout(function() {
          if (token !== swapToken) return;
          // Geometry and pixels in one frame, both while the band is
          // invisible. is-image-swapping also drops the transform from the
          // ::before's transition list, so the shift lands as a jump rather
          // than as a slide - and the reflow below commits it while the
          // class is still on, so removing the class cannot start one
          // either: by then the transform already holds its new value.
          applyBandFit(fitImgEl);
          tile.style.setProperty('--longoni-image', value);
          void tile.offsetWidth;
          tile.classList.remove('is-image-swapping');
        }, 140);
      };
      if (!targetSrc) { startFade(); return; }
      var applied = false;
      var applyOnce = function() { if (applied) return; applied = true; startFade(); };
      /*
        A TILE KEEPS THE PICTURE IT HAS WHEN THE NEW ONE WILL NOT COME
        (2026-10-17, the same audit). onerror used to run the swap, so a
        refused file was painted as a broken image on the card. The swap is
        simply not made now - the tile goes on showing the option it was
        showing, which is the honest failure for a preview.
      */
      var preload = new Image();
      preload.onload = applyOnce;
      preload.onerror = function() { applied = true; };
      preload.src = targetSrc;
      if (preload.complete) {
        applyOnce();
      } else {
        if (preload.decode) preload.decode().then(applyOnce, function() {});
        window.setTimeout(applyOnce, 1200);
      }
    }

    function setPreviewRows(sources) {
      window.clearTimeout(imageTimer);
      var unchanged = previewRows.every(function(img, index) {
        var source = sources[index] || '';
        return img.getAttribute('src') === source && (img.style.visibility === 'hidden') === !source;
      });
      if (unchanged) {
        tile.classList.remove('is-image-swapping');
        return;
      }
      if (prefersReducedMotion()) {
        applyPreviewRows(sources);
        return;
      }
      // Same decode-then-swap shape as setTileImage above: this is
      // McDermott's three-row cue composite, heavier than Longoni's single
      // background-image swap, so it is the one most likely to still be
      // mid-decode when a fast hover-then-click opens the lightbox. Waiting
      // for every row to decode before fading keeps that decode work from
      // landing on top of (or racing) the modal's own image load.
      var token = ++swapToken;
      var targets = sources.filter(Boolean);
      var startFade = function() {
        if (token !== swapToken) return;
        tile.classList.add('is-image-swapping');
        imageTimer = window.setTimeout(function() {
          if (token !== swapToken) return;
          applyPreviewRows(sources);
          tile.classList.remove('is-image-swapping');
        }, 140);
      };
      if (!targets.length) { startFade(); return; }
      var remaining = targets.length;
      var settled = false;
      var oneRowReady = function() {
        remaining--;
        if (remaining <= 0 && !settled) { settled = true; startFade(); }
      };
      targets.forEach(function(src) {
        // Each row gets its own once-guard - onload/onerror and an
        // already-.complete image can both fire for the same load, and
        // without the guard that row would decrement remaining twice.
        var rowApplied = false;
        var rowApplyOnce = function() { if (rowApplied) return; rowApplied = true; oneRowReady(); };
        var preload = new Image();
        preload.onload = rowApplyOnce;
        preload.onerror = rowApplyOnce;
        preload.src = src;
        if (preload.complete) {
          rowApplyOnce();
        } else if (preload.decode) {
          preload.decode().then(rowApplyOnce, function() {});
        }
      });
      window.setTimeout(function() { if (!settled) { settled = true; startFade(); } }, 1200);
    }

    function getTileItems() {
      if (items) return items;
      var trigger = tile.querySelector('[data-longoni-gallery-trigger]');
      var gallery = trigger && document.getElementById(trigger.getAttribute('data-longoni-gallery-trigger'));
      items = gallery ? getItems(gallery) : [];
      return items;
    }

    function previewPartsFor(item, label, alt, min) {
      var variants = (item.product && item.product.variants) || [];
      var pool = variants.filter(function(v) { return v.available; });
      if (!pool.length) pool = variants;
      var distinct = {};
      pool.forEach(function(v) { distinct[v.price] = true; });
      var figure = Object.keys(distinct).length > 1
        ? fromTemplate.replace('[PRICE]', item.price)
        : item.price;
      /*
        THE LABEL FOLLOWS THE SELECTION (2026-09-28, the owner's brief).
        "Starting from" answers the question "is there a choice of price
        here", and once a swatch is chosen there is no choice left: G323
        showed .50 with Starting from still over it. So the label is
        the SELECTED option's, exactly as the price, the strips, the config
        line and the BUY target already are.

        What an option's label is: its own name, the same string the
        caption under its swatch shows - G-Core shaft, DEFY carbon shaft,
        Hard Maple shaft, i-2 shaft, 2x S20 shafts, No Wrap, 13.75 mm - and
        for a shaft-only option the wording the theme already had for it,
        "<type> Shaft Only". Every swatch carries it as
        data-longoni-eyebrow now (2026-09-29, the owner's brief: the slot
        that holds Starting from names the hovered option instead of going
        empty). It fits: the longest of them, DEFY carbon shaft, is 86.9px
        of ink at 10px against the narrowest slot this theme has, 94.7px at
        1024 - measured on every rail at 2560, 1920, 1440, 1024 and 375 -
        so nothing is shortened, wrapped, shrunk or cut anywhere. The
        slot's height is fixed in CSS at every breakpoint (14px, 11px above
        1850), so no card can change height either way.

        At rest the slot holds what the server rendered, which is Starting
        from where the tile has two or more distinct prices and nothing
        where it has one. Leaving the grid restores it through restorePrice
        together with the figure, so the label and the price can never
        disagree.

        This replaces the rule of earlier the same day - "the eyebrow is
        the price's label, never the option's name" - which was written
        against desc leaking into the slot (Black Mamba II read Luna Shaft
        at rest). desc still never lands here: the callers pass the
        option's own eyebrow or an empty string, never its description.
      */
      return { eyebrow: (label == null ? (eyebrowEl ? eyebrowEl.textContent : '') : label), eyebrowAlt: alt, eyebrowMin: min, figure: figure };
    }

    /*
      THE SLOT TAKES THE LONGEST FORM THAT FITS (2026-09-29, the owner's
      order: full, then "carbon" dropped, then "shaft" dropped). The slot is
      not one width - 155.5px at 1920 and 2560, 116.6 at 375, 108.2 at 1440,
      94.7 at 1024 - and "w/ DEFY carbon shaft" is 101.9px, "w/ Hard Maple
      shaft" 95.7, so both overflow at 1024 and nothing else does anywhere.
      The three forms come from the swatch, built in Liquid out of the
      catalogue's own words; this only measures and chooses, so nothing
      wraps, shrinks or is cut.
    */
    function fitEyebrow(text, alt, min) {
      if (!eyebrowEl || !text) return text;
      eyebrowEl.textContent = text;
      if (eyebrowEl.scrollWidth <= eyebrowEl.clientWidth + 0.5) return text;
      if (alt && alt !== text) {
        eyebrowEl.textContent = alt;
        if (eyebrowEl.scrollWidth <= eyebrowEl.clientWidth + 0.5) return alt;
      }
      if (min) { eyebrowEl.textContent = min; return min; }
      return alt || text;
    }

    function applyPrice(parts) {
      if (eyebrowEl && figureEl) {
        eyebrowEl.textContent = fitEyebrow(parts.eyebrow, parts.eyebrowAlt, parts.eyebrowMin);
        if (parts.figureHtml != null) {
          figureEl.innerHTML = parts.figureHtml;
        } else if (figureKeepsHidePrice) {
          figureEl.textContent = '';
          var hideSpan = document.createElement('span');
          hideSpan.className = 'hidePrice';
          hideSpan.textContent = parts.figure;
          figureEl.appendChild(hideSpan);
        } else {
          figureEl.textContent = parts.figure;
        }
      } else {
        priceEl.textContent = (parts.eyebrow ? parts.eyebrow + ' ' : '') + parts.figure;
      }
    }

    function priceMatches(parts) {
      if (eyebrowEl && figureEl) {
        var cur = eyebrowEl.textContent;
        var same = cur === parts.eyebrow || (parts.eyebrowAlt && cur === parts.eyebrowAlt) || (parts.eyebrowMin && cur === parts.eyebrowMin);
        return same && figureEl.textContent === parts.figure;
      }
      return priceEl.textContent === (parts.eyebrow ? parts.eyebrow + ' ' : '') + parts.figure;
    }

    function setPriceParts(parts) {
      window.clearTimeout(swapTimer);
      if (priceMatches(parts)) {
        // The cancelled timer was the only thing that would have removed
        // the fade class - without this the price stays at opacity 0 after
        // a hover that leaves before the 140ms apply ever runs. Same
        // unchanged-branch cleanup setTileImage and setPreviewRows do.
        priceEl.classList.remove('is-swapping');
        return;
      }
      if (prefersReducedMotion()) {
        applyPrice(parts);
        return;
      }
      priceEl.classList.add('is-swapping');
      swapTimer = window.setTimeout(function() {
        applyPrice(parts);
        priceEl.classList.remove('is-swapping');
      }, 140);
    }

    function setActiveThumb(thumb) {
      var thumbs = tile.querySelectorAll('.longoni-promo-strip__mini-button--thumb');
      Array.prototype.forEach.call(thumbs, function(candidate) {
        var active = candidate === thumb;
        candidate.classList.toggle('is-active', active);
        if (active) candidate.setAttribute('aria-current', 'true');
        else candidate.removeAttribute('aria-current');
      });
      // Free-case bar follows the active option: shown only when that
      // option's product ships with a case (data-longoni-case set in Liquid).
      var caseBar = tile.querySelector('[data-longoni-case-bar]');
      if (caseBar && thumb) caseBar.hidden = !thumb.getAttribute('data-longoni-case');
      /*
        A swatch that sells the shaft ALONE marks the tile, and the rest is
        stylesheet: the butt rows fade to 0.28 and the FREE CASE bar goes
        out by visibility (a free case ships with a cue, not with a shaft).
        Toggled here rather than anywhere else because this runs inside
        commitPreviewState's single frame, so the fade, the bands, the
        price and the button all land together - and restore, which passes
        the default thumb through this same path, takes it back off.
      */
      if (thumb) tile.classList.toggle('is-shaft-only', thumb.getAttribute('data-longoni-shaft-only') === 'true');
    }

    /*
      BUY or SOLD OUT, decided by the option now selected.

      The item's variants are the source, so this is the same rule the panel
      uses and it needs nothing per row. data-longoni-available on the swatch
      is the fallback for the path with no gallery item to read - Liquid
      writes it from the option product's own availability - and if neither is
      there the tile keeps whichever state the server rendered.
    */
    function setBuyState(item, thumb) {
      if (!soldOutEl || !cta) return;
      var buyable;
      if (item && item.product) {
        buyable = isBuyable(item.product);
      } else if (thumb && thumb.hasAttribute('data-longoni-available')) {
        buyable = thumb.getAttribute('data-longoni-available') !== 'false';
      } else {
        return;
      }
      cta.hidden = !buyable;
      soldOutEl.hidden = buyable;
      /*
        AND THE BUTTON SAYS WHAT IT BUYS.

        Every tile ships "BUY <model>" in the markup, in two spans - the
        desktop one and the phone one - and a shaft swatch is the first
        option whose button must say something else: the link points at
        the shaft product, so the label has to as well ("BUY SHAFT").

        The server-rendered text is stashed on the span the first time it
        is touched, never read from a constant, so restore puts back
        whatever that tile actually had - including the stepped-down
        is-long variant and the Longoni row's short "BUY".
      */
      var buyLabel = thumb && thumb.getAttribute('data-longoni-buy-label');
      Array.prototype.forEach.call(cta.querySelectorAll('.longoni-promo-strip__button-label'), function (span) {
        if (span.getAttribute('data-longoni-label-default') === null) {
          span.setAttribute('data-longoni-label-default', span.textContent);
        }
        var next = buyLabel || span.getAttribute('data-longoni-label-default');
        if (span.textContent !== next) span.textContent = next;
      });
      /*
        AND THE CONFIG LINE SAYS WHAT IS SELECTED (2026-09-26, the Ambizione
        and Randers tiles). The same rule as the button's label above: the
        server-rendered text is stashed the first time it is touched, so a
        restore puts back whatever that tile actually had. A tile without the
        line - every carom tile that shipped before - has nothing to find and
        nothing happens.
      */
      var configEl = tile.querySelector('[data-longoni-config-line]');
      if (configEl) {
        if (configEl.getAttribute('data-longoni-config-default') === null) {
          configEl.setAttribute('data-longoni-config-default', configEl.textContent);
        }
        var configFromThumb = thumb && thumb.getAttribute('data-longoni-config');
        var configText = configFromThumb || configEl.getAttribute('data-longoni-config-default');
        if (configEl.textContent !== configText) configEl.textContent = configText;
      }
    }

    /*
      ONE STATE, ONE FRAME.

      showPreview used to set the swatch, the price and the button link
      straight away and only then hand the bands to setPreviewRows, which
      waits on decode and fades over 140ms. So a variant arrived in two
      instalments - label and price first, shaft and case after - which is
      the "appears separately" this replaces.

      Now nothing moves until every band of the TARGET state can be
      painted: they are decoded first, capped at 400ms so a cold image
      cannot hold the card (the cap the rubber row uses), and then the
      whole state - swatch, price, link, bands - is applied inside one
      requestAnimationFrame.

      swapToken is the race guard, shared with setPreviewRows: only the
      most recently requested state may apply, and a second click on the
      chip already in flight is not a new flight, so a fast series settles
      on the last one asked for rather than the last one to finish
      decoding.
    */
    var GATE_MS = 400;

    function commitPreviewState(thumb, item, sources, commit) {
      var token = ++swapToken;
      var urls = (sources || []).filter(Boolean);
      var applied = false;

      if (!prefersReducedMotion()) tile.classList.add('is-image-swapping');

      var apply = function () {
        if (applied || token !== swapToken) return;
        applied = true;
        window.requestAnimationFrame(function () {
          if (token !== swapToken) return;
          setActiveThumb(thumb);
          var parts = null;
          if (item && item.price) {
            parts = previewPartsFor(item, thumb.getAttribute('data-longoni-eyebrow') || '', thumb.getAttribute('data-longoni-eyebrow-alt') || '', thumb.getAttribute('data-longoni-eyebrow-min') || '');
            setPriceParts(parts);
          } else if (!item) {
            setPriceParts(originalParts);
          }
          if (cta) {
            var href = (item && item.url) ? item.url : originalCtaUrl;
            if (href) cta.setAttribute('href', href);
          }
          setBuyState(item, thumb);
          applyPreviewRows(sources);
          tile.classList.remove('is-image-swapping');

          /*
            A COMMIT MAKES THIS STATE THE DEFAULT (2026-10-12, the owner's
            decision: a tap or click sticks). Every restore in this file goes
            back to six things captured once at bind - defaultActiveThumb,
            originalParts, originalCtaUrl, defaultPreviewRows, and the label
            and config defaults stashed on the DOM - so a selection could never
            outlive the next pointerleave or blur. They are re-captured here,
            from the state that has just been applied, in the same frame, so
            hover may still preview another option and the one revert the
            rails live by (pointerleave of the chip strip) returns to the
            CHOSEN option rather than the server's.

            originalParts takes the parts object, not the DOM: applyPrice
            rebuilds the wholesale hidePrice span from figureKeepsHidePrice
            exactly as it does for a preview, and the price lands on its own
            140ms timer, so reading the figure back here would read the old
            one. The tile's own URL - the body click's destination - moves
            with the BUY href, and the whole-card overlay follows the href on
            its own observer.
          */
          if (commit) {
            defaultActiveThumb = thumb;
            if (parts) originalParts = parts;
            if (cta && cta.getAttribute('href')) originalCtaUrl = cta.getAttribute('href');
            defaultPreviewRows = (sources || []).map(function (source) { return source || ''; });
            if (originalCtaUrl) tile.setAttribute('data-longoni-tile-url', originalCtaUrl);
            if (cta) {
              Array.prototype.forEach.call(cta.querySelectorAll('.longoni-promo-strip__button-label'), function (span) {
                span.setAttribute('data-longoni-label-default', span.textContent);
              });
            }
            var committedConfig = tile.querySelector('[data-longoni-config-line]');
            if (committedConfig) committedConfig.setAttribute('data-longoni-config-default', committedConfig.textContent);
          }
        });
      };

      if (!urls.length) { apply(); return; }

      var waits = urls.map(function (url) {
        return new Promise(function (resolve) {
          var image = new Image();
          image.src = url;
          if (image.decode) { image.decode().then(resolve, function () { resolve(); }); return; }
          if (image.complete) { resolve(); return; }
          image.onload = resolve;
          image.onerror = resolve;
        });
      });
      Promise.all(waits).then(apply, apply);
      // The ceiling. A band that is still on the network must not keep the
      // reader looking at the state they just left.
      window.setTimeout(apply, GATE_MS);
    }

    function showPreview(thumb) {
      var tileItems = getTileItems();
      if (!tileItems.length) return;
      var index = parseInt(thumb.getAttribute('data-longoni-gallery-index') || '0', 10);
      var item = tileItems[Math.max(0, Math.min(index, tileItems.length - 1))];
      if (!item) return;
      if (previewRows.length) {
        preloadPreviewRows();
        commitPreviewState(thumb, item, rowSourcesOf(thumb));
        return;
      }
      setActiveThumb(thumb);
      if (item.price) {
        setPriceParts(previewPartsFor(item, thumb.getAttribute('data-longoni-eyebrow') || '', thumb.getAttribute('data-longoni-eyebrow-alt') || '', thumb.getAttribute('data-longoni-eyebrow-min') || ''));
      }
      if (cta && item.url) cta.setAttribute('href', item.url);
      setBuyState(item, thumb);
      // No hover gate: on touch this same routine is what the first tap on
      // a swatch runs, and the preview has to swap there too.
      if (item.tileSrc) {
        preloadTileImages();
        setTileImage("url('" + item.tileSrc + "')", thumb.querySelector('img'));
      }
    }

    function restorePrice() {
      setPriceParts(originalParts);
      setTileImage(originalImage, findOriginalFitImg());
    }

    function restorePreview() {
      // Leaving is a state change like any other, so it goes through the
      // same gate: the default bands are decoded before the swatch, price
      // and link move back, and it takes the same token, so a hover that
      // leaves and returns cannot land out of order.
      if (previewRows.length) {
        commitPreviewState(defaultActiveThumb, null, defaultPreviewRows);
        setTileImage(originalImage, findOriginalFitImg());
        return;
      }
      if (defaultActiveThumb) setActiveThumb(defaultActiveThumb);
      if (cta && originalCtaUrl) cta.setAttribute('href', originalCtaUrl);
      setBuyState(null, defaultActiveThumb);
      restorePrice();
    }

    /*
      THE CLICK STICKS (2026-10-12, the owner's decision). Hover may preview;
      a click or a tap on a chip COMMITS it - the price, the BUY target, the
      config line and the card's own link all move to that option and stay
      there, and the next click on the same chip opens the viewer on it. It
      used to be two behaviours: touch tapped the preview on and nothing put
      it back, desktop opened the viewer and the hover state reverted under
      it, so on a mouse - and on any tablet that reports hover - the second
      diameter of a VQ1 could not be chosen at all and BUY sold the first.

      A sold-out option is never committed: the default is never a sold-out
      product, and the chips mark one with is-sold-out on their variant
      wrapper. Such a click falls through to the viewer, which is what a
      shopper can do with an option they cannot buy. Returns true when it
      committed, so the caller knows not to open anything.
    */
    function commitSelection(thumb) {
      if (!thumb || thumb.closest('.is-sold-out')) return false;
      /* The chip the shopper is already resting on opens the viewer instead.
         Measured against defaultActiveThumb, never against is-active: on a
         mouse the hover that precedes every click has already run
         setActiveThumb, so the clicked chip IS is-active by the time the
         click lands - which is exactly how the first build of this fell
         through to the viewer on desktop while committing on touch. */
      if (thumb === defaultActiveThumb) return false;
      var tileItems = getTileItems();
      if (!tileItems.length) return false;
      var index = parseInt(thumb.getAttribute('data-longoni-gallery-index') || '0', 10);
      var item = tileItems[Math.max(0, Math.min(index, tileItems.length - 1))];
      if (!item) return false;
      if (item.product && !isBuyable(item.product)) return false;
      if (previewRows.length) {
        preloadPreviewRows();
        commitPreviewState(thumb, item, rowSourcesOf(thumb), true);
        return true;
      }
      // the rows-less path no section takes today: commit through the same
      // state function so the capture happens in one place
      commitPreviewState(thumb, item, [], true);
      return true;
    }

    /*
      preloadPreviewRows() used to run right here, at tile init, for every
      tile on the page. It fetches each swatch's three band images through
      new Image(), and new Image() answers to nothing: not loading="lazy",
      not the viewport, not whether the tile is even on screen. So the
      McDermott section pulled every band of all thirteen tiles - both rows
      below the fold included, and every hover-only variant, the DEFY shaft
      and all three Vanquish wraps - about two seconds after load. Measured
      on the draft at 1280x720, scrolled to the top: 55 files, 1063KB.

      The swatch thumbnails in those same tiles are plain <img loading=
      "lazy"> and only four pairs of them loaded, which is the answer to
      whether native lazy copes with a horizontal rail: it does. The bands
      were not lazy-loading badly, they were not lazy-loading at all.

      showPreviewRows() calls this on the way into a preview, so the first
      hover on a tile still warms all three of its rows before the second
      swatch is tried. What is gone is paying for that on every tile in the
      section whether or not anyone points at it.
    */

    /*
      The band fit is otherwise only computed when a photo is swapped in,
      which leaves the state the card actually loads in - the default photo
      - unmeasured, and on phones that is the state whose shift matters
      most. findOriginalFitImg resolves the thumb whose image IS the
      default photo, so it is measured from the same already-decoded chip
      image as every other state.

      Re-run when the phone breakpoint is crossed: the shift is in px off a
      measurement in px, so it does not survive a width change on its own,
      and the desktop path wants the vars cleared rather than kept.
    */
    // The thumb whose image is the one the band is showing right now,
    // default or previewed - the same lookup findOriginalFitImg does, but
    // against the current value rather than the original.
    function currentFitImg() {
      var current = tile.style.getPropertyValue('--longoni-image') || originalImage;
      var urlMatch = (current || '').match(/url\(['"]?([^'")]+)/);
      if (!urlMatch) return findOriginalFitImg();
      var tileItems = getTileItems();
      var thumbEls = tile.querySelectorAll('.longoni-promo-strip__mini-button--thumb');
      for (var i = 0; i < thumbEls.length; i++) {
        var idx = parseInt(thumbEls[i].getAttribute('data-longoni-gallery-index') || '-1', 10);
        if (tileItems[idx] && tileItems[idx].tileSrc === urlMatch[1]) return thumbEls[i].querySelector('img');
      }
      return findOriginalFitImg();
    }

    applyBandFit(findOriginalFitImg());
    if (phoneBand) {
      var onPhoneBandChange = function() {
        applyBandFit(currentFitImg());
      };
      if (phoneBand.addEventListener) phoneBand.addEventListener('change', onPhoneBandChange);
      else if (phoneBand.addListener) phoneBand.addListener(onPhoneBandChange);
    }

    var thumbs = Array.prototype.slice.call(tile.querySelectorAll('.longoni-promo-strip__mini-button--thumb'));
    // The one revert (2026-09-24, the owner's rule for every card with
    // swatches): the pointer leaves the chip STRIP - pointerleave on
    // .longoni-promo-strip__actions, which holds every chip, the gaps
    // between them and the loupe. It was mouseleave on each chip, so the
    // gap between two chips put the default band back and the next chip
    // swapped it forward again - the flicker the owner reported on the
    // Longoni tiles, the same bug as the family cards' swatch gaps.
    var chipStrip = tile.querySelector('.longoni-promo-strip__actions');
    if (hoverCapable && chipStrip) chipStrip.addEventListener('pointerleave', restorePreview);
    thumbs.forEach(function(thumb) {
      if (hoverCapable) {
        thumb.addEventListener('mouseenter', function() { showPreview(thumb); });
      }
      // Tapping a link focuses it, same as tabbing to it does - unlike
      // mouseenter, focus was never gated to hoverCapable, so every touch
      // tap fired it as well as the delegated click handler's own
      // showPreview call below (touchOnly branch), and the leave-focus
      // that follows fired restorePreview in between: two full swap
      // cycles plus a revert racing for the same tap, the concrete cause
      // of the band photos re-switching several times before settling.
      // :focus-visible is the standard way to tell them apart - true for
      // real keyboard tabbing, false for the focus a pointer tap also
      // produces (same idiom this file already uses for the lightbox
      // controls' focus ring). Only keyboard focus drives the preview;
      // pointer taps are left entirely to the click handler.
      var focusWasVisible = false;
      thumb.addEventListener('focus', function() {
        try {
          focusWasVisible = !!(thumb.matches && thumb.matches(':focus-visible'));
        } catch (error) {
          // :focus-visible unsupported: fall back to the old always-on
          // behaviour rather than silently dropping keyboard preview.
          focusWasVisible = true;
        }
        if (focusWasVisible) showPreview(thumb);
      });
      thumb.addEventListener('blur', function() {
        if (focusWasVisible) restorePreview();
        focusWasVisible = false;
      });
    });

    // Touch translation of the hover mechanic: the gallery-trigger click
    // handler reads this to run the preview on the first tap of a swatch
    // and only open the viewer on a tap of the already-active one.
    tile.__longoniShowPreview = showPreview;
    tile.__longoniCommitSelection = commitSelection;
    // Read by the same click handler right before it opens the modal: a
    // hover fires on mouse-down-then-click before the click itself, so a
    // hover-then-click sequence can leave a decode still in flight when the
    // modal opens. Bumping the token here invalidates it - whichever
    // decode/timeout resolves later becomes a no-op - so the tile's own
    // preview swap never mutates DOM or decodes images on top of the
    // lightbox's own image load. This was the concrete cause of McDermott's
    // heavier three-row swap (setPreviewRows) stuttering the lightbox open
    // where Longoni's single background-image swap barely registered.
    tile.__longoniCancelPreview = function() {
      swapToken++;
      window.clearTimeout(imageTimer);
      // A cached image's decode can resolve synchronously, in the same
      // tick as the mouseenter that started it - startFade() then adds
      // is-image-swapping before this click handler ever runs, and
      // cancelling only the timer left the tile faded out with nothing
      // left to schedule its removal. Strip it here too so cancelling
      // always leaves the tile at full visibility, not mid-fade forever.
      tile.classList.remove('is-image-swapping');
    };
  }

  function initRoot(root) {
    if (!root || root.dataset.longoniLightboxReady === 'true') return;
    root.dataset.longoniLightboxReady = 'true';

    Array.prototype.slice.call(root.querySelectorAll(TRIGGER_SELECTOR)).forEach(function(trigger) {
      trigger.addEventListener('click', function(event) {
        event.preventDefault();
        event.stopPropagation();

        // A chip's two roles split across clicks, on every input alike
        // (2026-10-12): the first click on a not-yet-active chip COMMITS it
        // - see commitSelection - and a click on the active one opens the
        // viewer on that option. It was only touch that stuck, and only as a
        // preview nothing ever put back; a mouse click opened the viewer and
        // lost the hover state under it.
        if (trigger.classList.contains('longoni-promo-strip__mini-button--thumb')) {
          var tile = trigger.closest('.longoni-promo-strip__tile');
          if (tile && tile.__longoniCommitSelection && tile.__longoniCommitSelection(trigger)) return;
        }

        // A hover fires before this click on desktop; cancel whatever
        // preview swap it started so it can't decode/mutate images on top
        // of the modal that is about to open (see __longoniCancelPreview).
        var openingTile = trigger.closest('.longoni-promo-strip__tile');
        if (openingTile && openingTile.__longoniCancelPreview) openingTile.__longoniCancelPreview();

        /*
          THE LOUPE OPENS ON THE OPTION THE TILE IS SHOWING (2026-09-14).

          A swatch carries its own data-longoni-gallery-index; the loupe
          carries none, so it always opened on option 0 - on Lucky L1 with
          SHAFT ONLY chosen the viewer showed the whole cue, on Black Rainbow
          with Butt chosen the S20 set. The selected option is the swatch
          holding .is-active (setActiveThumb, above: the tapped one on touch,
          the default or the one still previewed on desktop), and its index
          is already the gallery's, so the viewer starts there with every
          option and every frame still in reach. No active swatch of this
          gallery (the case tiles have none) opens on the first, as before.
        */
        var galleryId = trigger.getAttribute('data-longoni-gallery-trigger');
        var startIndex = trigger.getAttribute('data-longoni-gallery-index');
        if (startIndex === null && openingTile) {
          var activeThumb = openingTile.querySelector('.longoni-promo-strip__mini-button--thumb.is-active[data-longoni-gallery-index]');
          if (activeThumb && activeThumb.getAttribute('data-longoni-gallery-trigger') === galleryId) {
            startIndex = activeThumb.getAttribute('data-longoni-gallery-index');
          }
        }
        /*
          The family cards (sections/chalk-cards.liquid with its loupe on -
          Cue Tip Tools) are not promo tiles: their selection is the active
          chip of the active tab's panel, and each chip carries its gallery
          position as data-bc-gallery-index. Same rule, other markup.
        */
        if (startIndex === null) {
          var familyCard = trigger.closest('[data-bc-gallery-scope]');
          var activeChip = familyCard && familyCard.querySelector('[data-simonis-size-panel].is-active [data-bc-gallery-index].is-active');
          if (activeChip) startIndex = activeChip.getAttribute('data-bc-gallery-index');
        }

        openModal(galleryId, startIndex || 0, trigger);
      });
    });

    Array.prototype.slice.call(root.querySelectorAll('.longoni-promo-strip__tile')).forEach(function(tile) {
      bindPricePreview(tile);
      tile.addEventListener('click', function(event) {
        var controlTarget = event.target && event.target.closest ? event.target.closest('[data-longoni-control], a, button') : null;
        if (controlTarget) return;
        var tileUrl = tile.getAttribute('data-longoni-tile-url');
        if (tileUrl) window.location.href = tileUrl;
      });
      tile.addEventListener('keydown', function(event) {
        var controlTarget = event.target && event.target.closest ? event.target.closest('[data-longoni-control], a, button') : null;
        if (controlTarget || (event.key !== 'Enter' && event.key !== ' ')) return;
        event.preventDefault();
        var tileUrl = tile.getAttribute('data-longoni-tile-url');
        if (tileUrl) window.location.href = tileUrl;
      });
    });
  }

  function initAllRoots() {
    Array.prototype.slice.call(
      document.querySelectorAll(ROOT_SELECTOR + ', [data-mcdermott-tiles], [data-case-tiles], [data-bc-lightbox-root]')
    ).forEach(initRoot);
  }

  /*
    --bc-vh: one hundredth of the height the page can actually see, in px
    (2026-09-12). On iOS 100vh is the height with the address bar hidden, so
    a dialog sized in vh ran past the bottom of the screen and cut the photo
    whenever the bar was showing; dvh fixes that where it exists and is
    ignored where it does not. innerHeight is right in both, so the viewer
    sizes against calc(var(--bc-vh) * 100) with 1vh as the fallback.
  */
  function setViewportUnit() {
    var h = window.innerHeight || document.documentElement.clientHeight;
    if (h) document.documentElement.style.setProperty('--bc-vh', (h / 100) + 'px');
  }

  function init() {
    setViewportUnit();
    window.addEventListener('resize', setViewportUnit);
    window.addEventListener('orientationchange', setViewportUnit);
    if (window.visualViewport) window.visualViewport.addEventListener('resize', setViewportUnit);
    initAllRoots();
    document.addEventListener('keydown', handleKeydown);
    /*
      The title reserve is a wrap measurement, so it belongs to the width it
      was taken at. Rotating a phone re-wraps the name and the reservation
      has to be retaken - debounced, and only while the dialog is actually
      open, so a page with no viewer open pays nothing.
    */
    var reserveTimer = 0;
    var retakeReserve = function() {
      if (!modal || modal.hidden) return;
      window.clearTimeout(reserveTimer);
      reserveTimer = window.setTimeout(reserveTitleHeight, 120);
    };
    window.addEventListener('resize', retakeReserve);
    window.addEventListener('orientationchange', retakeReserve);
    // The case rows arrive after the fact - fetched by their lazy slots,
    // which announce themselves with shopify:section:load. initRoot is
    // guarded by a dataset flag, so re-scanning is idempotent.
    document.addEventListener('shopify:section:load', initAllRoots);
    // A bfcache snapshot can be taken mid-fade (hover a swatch, then click
    // through before the 140ms apply): the snapshot keeps the fade class
    // but not the timer that removes it, so Back would restore the tile
    // with its price or image held invisible.
    window.addEventListener('pageshow', function(event) {
      if (!event.persisted) return;
      Array.prototype.slice.call(
        document.querySelectorAll('.longoni-promo-strip__price.is-swapping, .longoni-promo-strip__tile.is-image-swapping')
      ).forEach(function(el) {
        el.classList.remove('is-swapping');
        el.classList.remove('is-image-swapping');
      });
    });
  }

  /*
    The page-wide handle on this viewer: the guard at the top of the file
    reads it to keep a second copy from starting, and a script outside can
    open a gallery with it. Its first caller, the family-card loupe
    (chalk, balls, tips, gloves), was removed on 2026-09-15.
  */
  window.BCLongoniLightbox = { open: openModal };

  ready(init);
})();
/* sync touch 2026-09-28 */
